import { HttpClient } from '@angular/common/http';
import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ActivatedRoute, Event, NavigationStart, Router } from '@angular/router';
import {
    SalesDocument,
    SalesDocumentPosition,
    SalesDocumentType,
    ProductsList,
    PriceVersion,
    DocumentSummarize,
    TaxRatesService,
    Discount,
    DocumentErrors,
    PersonsService,
    DocumentIntegrityService,
    DocumentDifference,
    Synced,
    State,
    DocumentStatus,
} from '@icc/b2b/shared';
import { ExchangeRateService, Currency, ExchangeRate } from '@icc/shared/currency';
import { ConfigService, DynamicElementsService } from '@icc/shared';
import { DialogService } from '@icc/ui';
import { NgxPermissionsService } from 'ngx-permissions';
import { Subscription, firstValueFrom, lastValueFrom } from 'rxjs';
import { SalesDocumentViewMode } from '../models/view-modes.model';
import { SalesDocumentService } from '../sales-document.service';
import { DocumentSummary } from 'libs/b2b/shared/src/lib/models/document-summary.model';
import { CompanyService } from '../company.service';
import { core, logger, ToastService, _, getUnitInfo } from '@icc/shared/helpers/b2b';
import { TranslateService } from '@ngx-translate/core';
import { validationDocument } from '../validators/document';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { CreateOrderDialogComponent } from '../create-order-dialog/create-order-dialog.component';
import { ConfirmOrderDialogComponent } from '../confirm-order-dialog/confirm-order-dialog.component';
import { OwnProductsDialogComponent } from '../own-products-dialog/own-products-dialog.component';
import { PriceVersionService } from '../price-version-selector/price-version.service';
import { OfferValidityService } from '../offer-validity/offer-validity.service';
import { SyncService } from '../sync.service';
import { filter, map, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { getDocumentsAttachments, getSalesDocument, getSalesDocumentsEntities, getSynced } from '../+state/sales-documents.selectors';
import { DocumentValuationService } from '../document-valuation.service';
import { SendValuationDialogComponent } from '../send-valuation-dialog/send-valuation-dialog.component';
import { ModifyOrderDialogComponent } from '../modify-order-dialog/modify-order-dialog.component';
import { CancelOrderDialogComponent } from '../cancel-order-dialog/cancel-order-dialog.component';
import { DocumentManagerService } from '../document-manager.service';
import { DocumentCreationService } from '../document-creation.service';
import { DocumentPricesService } from '../document-prices.service';
import { B2bDocumentPricesService } from '../b2b-document-prices.service';
import { PriceVersionDialogComponent } from '../price-version-dialog/price-version-dialog.component';
import { OwnProductsService } from '../own-products.service';
import { ConfiguratorLoaderService } from '@icc/b2b/configurator';
import { PositionService } from '../position.service';
import { DocumentAction } from './document-action';
import { IccAppearance } from 'libs/ui/src/lib/enums/appearance';
import { BreakpointObserver } from '@angular/cdk/layout';
import { PrintTemplatedDialogComponent } from '../print-templated-dialog/print-templated-dialog.component';
import { startBuffering, stopBuffering } from '../+state/sales-documents.actions';
import * as Sentry from '@sentry/angular-ivy';

@Component({
    selector: 'icc-sales-document-summary',
    templateUrl: './sales-document-summary.component.html',
    styleUrls: ['./sales-document-summary.component.scss'],
})
export class SalesDocumentSummaryComponent implements OnInit, OnChanges, OnDestroy {
    @Input() openedDocument: SalesDocument;
    @Input() salesDocument: SalesDocument | null;
    @Input() buyDocuments: SalesDocument[];
    @Input() positions: SalesDocumentPosition[];
    @Input() allPositions: SalesDocumentPosition[];
    @Input() buyPositions: SalesDocumentPosition[];
    @Input() allBuyPositions: SalesDocumentPosition[];
    @Input() currency: ExchangeRate;
    @Input() groupingSchemaId: number;
    @Input() summingGroupingSchemaId: number;
    @Input() buyPositionsMap: Map<string, SalesDocumentPosition>;
    @Input() allBuyPositionsMap: Map<string, SalesDocumentPosition>;
    @Input() connectedBuyPositions: Record<string, SalesDocumentPosition[]>;
    @Input() buyPositionsPerDocument: Record<string, SalesDocumentPosition[]>;
    @Input() sortingMode: string;

    @Output() modeChanged = new EventEmitter<SalesDocumentViewMode>();
    @Output() activeBuyDocumentChanged = new EventEmitter<SalesDocument | null>();

    openedDocumentsIds: string[] = [];

    priceVersionObj: PriceVersion;
    priceVersions: PriceVersion[] = [];
    actualPriceVersion: PriceVersion;
    mode: SalesDocumentViewMode = 'sale';
    private modeInit = false;
    activeBuyDocument: SalesDocument | null = null;
    invalidUnitsInDocument = false;
    loaded = false;
    tradePanelType: string;
    mobileView = false;

    currencies: Currency[] = [];
    buyCurrencies: Currency[] = [];
    exchangeRates: ExchangeRate[] = [];
    buyExchangeRates: ExchangeRate[] = [];
    salesCurrenciesFromBuy = false;
    canChangeSaleCurrency = false;

    calculationInProgress = false;
    salesDocumentSellerCompanyLicense = false;

    summary: {
        sales: DocumentSummary;
        buySales: DocumentSummary | null;
        buy: DocumentSummary | null;
        taxes: {
            taxRate: number;
            sales: DocumentSummary['taxes'][0];
            buySales: DocumentSummary['taxes'][0] | null;
            buy: DocumentSummary['taxes'][0] | null;
        }[];
        groupsPositions: {
            groupSchemaId: number;
            name: string;
            sales: DocumentSummary['groupsPositions'][0];
            buySales: DocumentSummary['groupsPositions'][0] | null;
            buy: DocumentSummary['groupsPositions'][0] | null;
        }[];
        groups: {
            key: string;
            name: string;
            sales: DocumentSummary['groupings'][0]['groups'][0];
            buySales: DocumentSummary['groupings'][0]['groups'][0] | null;
            buy: DocumentSummary['groupings'][0]['groups'][0] | null;
        }[];
        result: {
            value: number;
            margin: number;
            overhead: number;
        };
    };
    offerValidity;
    showOfferValidityWarning = false;
    invalidOffer = false;
    invalidVersion = false;
    invalidVersionEffectiveDate = false;
    invalidVersionExpiredDate = false;
    documentStatuses: DocumentStatus[] = [];

    changingActivePriceVersion: string | null = null;

    ownProducts: ProductsList;
    ownProductControl = new UntypedFormControl();

    priceUpdatePending$ = this.b2bDocumentPricesService.priceUpdatePending$;
    private subscriptions: Subscription[] = [];
    visibleValidDate: Date;
    showValidityInfo = true;
    processing = false;
    private currentDocumentId: string;
    syncedDocumentsState = new Map<string, Synced>();
    salesSynced: Synced = Synced.Offline;
    buySynced: Synced = Synced.Offline;
    synced: Synced = Synced.Offline;

    placingOrderInProgress = false;

    hasAccessToBuy = false;
    hasAccessToSale = false;
    integral = false;
    hasExternalVenetian = false;
    latestOrderId: string | null = null;

    hasAccessTo = {
        addPosition: false,
        addOwnPosition: false,
        saleSide: false,
        comparisionSide: false,
        buySide: false,
        confirm: false,
        sendQuotationEmailToClient: false,
        sendToClient: false,
        recalculate: false,
        modify: false,
        modifySales: false,
        copy: false,
        order: false,
        placeOrder: false,
        print: false,
        printExternalVenetianOrderPdf: false,
        cancel: false,
        cancelOrder: false,
        delete: false,
        calculationInfo: false,
        cantModifyInfo: false,
        transferChanges: false,
        buyCurrency: false,
        inquireValuation: false,
        sendValuation: false,
        viewBuyPrices: false,
        consult: false,
        editCode: false,
        editRequestedDeadline: false,
        copySpareParts: false,
        deletePositionsBothSides: false,
    };

    documentActions: DocumentAction[] = [
        {
            action: () => this.confirmDelete(this.salesDocument),
            icon: 'delete',
            show: () => this.hasAccessTo.delete,
            disabled: () => this.synced !== 'synced',
            label: _('INTERFACE|Usuń'),
            tooltip: undefined,
            appearance: IccAppearance.Secondary,
            side: 'left',
            testId: 'delete',
        },
        {
            action: () => this.confirmCancel(this.salesDocument),
            icon: 'close',
            show: () => this.hasAccessTo.cancel,
            disabled: () => this.synced !== 'synced',
            label: _('INTERFACE|Anuluj'),
            tooltip: undefined,
            appearance: IccAppearance.Outline,
            side: 'left',
            testId: 'cancel',
        },
        {
            action: () => this.confirmCancel(this.salesDocument || this.buyDocuments[0]),
            icon: 'close',
            show: () => this.hasAccessTo.cancelOrder,
            disabled: () => this.synced !== 'synced',
            label: _('SALES_DOCUMENT|Anuluj zamówienie'),
            tooltip: undefined,
            appearance: IccAppearance.Secondary,
            side: 'left',
            testId: 'cancel-order',
        },
        {
            action: () => this.confirmConfirm(this.salesDocument),
            icon: 'done',
            show: () => this.hasAccessTo.confirm,
            disabled: () => this.synced !== 'synced',
            label: _('SALES_DOCUMENT|Zatwierdź'),
            tooltip: undefined,
            appearance: IccAppearance.Primary,
            side: 'right',
            testId: 'confirm-button',
        },
        {
            action: () => this.inquireValuation(this.buyDocuments),
            icon: 'navigate_next',
            show: () => this.hasAccessTo.inquireValuation,
            disabled: () => this.synced !== 'synced',
            label: _('SALES_DOCUMENT|Poproś o wycenę'),
            tooltip: undefined,
            appearance: IccAppearance.Outline,
            side: 'right',
            testId: 'inquire-valuation-button',
        },
        {
            action: () => this.confirmSendValuation(this.salesDocument),
            icon: 'navigate_next',
            show: () => this.hasAccessTo.sendValuation,
            disabled: () => this.synced !== 'synced',
            label: _('SALES_DOCUMENT|Wyślij wycenę'),
            tooltip: undefined,
            appearance: IccAppearance.Primary,
            side: 'right',
            testId: 'send-valuation-button',
        },
        {
            action: () => this.confirmSendToContractor(this.salesDocument),
            icon: 'navigate_next',
            show: () => this.hasAccessTo.sendToClient,
            disabled: () => this.synced !== 'synced',
            label: _('SALES_DOCUMENT|Wyślij do klienta'),
            tooltip: undefined,
            appearance: IccAppearance.Primary,
            side: 'right',
            testId: 'send-to-client-button',
        },
        {
            action: () => this.sendQuotationEmailToClient(this.salesDocument),
            icon: 'navigate_next',
            show: () => this.hasAccessTo.sendQuotationEmailToClient,
            disabled: () => this.synced !== 'synced',
            label: _('SALES_DOCUMENT|Wyślij email do klienta'),
            tooltip: undefined,
            appearance: IccAppearance.Secondary,
            side: 'right',
            testId: 'send-quotation-email-to-client-button',
            children: [
                {
                    action: () => this.sendQuotationEmailToClient(this.salesDocument, false),
                    icon: 'navigate_next',
                    show: () => this.hasAccessTo.sendQuotationEmailToClient,
                    disabled: () => this.synced !== 'synced',
                    label: _('SALES_DOCUMENT|Przekazana klientowi'),
                    tooltip: undefined,
                    appearance: IccAppearance.Secondary,
                    side: 'right',
                    testId: 'send-quotation-email-to-client-button-2',
                },
            ]
        },
        {
            action: () => this.consult(this.buyDocuments),
            icon: 'info',
            show: () => this.hasAccessTo.consult,
            disabled: () => this.synced !== 'synced',
            label: _('SALES_DOCUMENT|Skonsultuj'),
            tooltip: undefined,
            appearance: IccAppearance.Secondary,
            side: 'right',
            testId: 'consult-button',
        },
        {
            action: () => this.confirmRecalculate(this.salesDocument),
            icon: 'calculate',
            show: () => this.hasAccessTo.recalculate,
            disabled: () => this.synced !== 'synced',
            label: _('SALES_DOCUMENT|Przelicz ceny'),
            tooltip: undefined,
            appearance: IccAppearance.Secondary,
            side: 'right',
            testId: 'recalculate-button',
        },
        {
            action: () => this.confirmModify(this.salesDocument),
            icon: 'edit',
            show: () => this.hasAccessTo.modify,
            disabled: () => this.synced !== 'synced',
            label: _('SALES_DOCUMENT|Modyfikuj'),
            tooltip: undefined,
            appearance: IccAppearance.Secondary,
            side: 'right',
            testId: 'modify-button',
        },
        {
            action: () => this.createSparePartsDocument(this.salesDocument),
            icon: 'navigate_next',
            show: () => this.hasAccessTo.copySpareParts,
            disabled: () => this.synced !== 'synced',
            label: _('SALES_DOCUMENT|Dobór do kompletu'),
            tooltip: undefined,
            appearance: IccAppearance.Secondary,
            side: 'right',
            testId: 'create-spare-parts-button',
        },
        {
            action: () => this.confirmModify(this.salesDocument, true),
            icon: 'edit',
            show: () => this.hasAccessTo.modifySales,
            disabled: () => this.synced !== 'synced',
            label: _('SALES_DOCUMENT|Modyfikuj sprzedaż'),
            tooltip: undefined,
            appearance: IccAppearance.Secondary,
            side: 'right',
            testId: 'modify-sales-button',
        },
        {
            action: () => this.copy(this.salesDocument),
            icon: 'copy',
            show: () => this.hasAccessTo.copy,
            disabled: () => this.synced !== 'synced',
            label: _('INTERFACE|Kopiuj'),
            tooltip: undefined,
            appearance: IccAppearance.Secondary,
            side: 'right',
            testId: 'copy-button',
        },
        {
            action: () => this.confirmOrder(this.salesDocument || this.openedDocument, this.salesDocument?.uuid),
            icon: 'done',
            show: () => this.hasAccessTo.order,
            disabled: () => this.synced !== 'synced' || this.placingOrderInProgress,
            label: _('SALES_DOCUMENT|Zamów'),
            tooltip: undefined,
            appearance: IccAppearance.Primary,
            side: 'right',
            testId: 'order-button',
        },
        {
            action: () => this.confirmOrder(this.salesDocument || this.openedDocument, this.salesDocument?.uuid),
            icon: 'done',
            show: () => this.hasAccessTo.placeOrder && !this.activeBuyDocument,
            disabled: () => this.synced !== 'synced' || this.placingOrderInProgress,
            label: _('SALES_DOCUMENT|Zamów'),
            tooltip: undefined,
            appearance: IccAppearance.Secondary,
            side: 'right',
            testId: 'place-order-button',
        },
        {
            action: () => this.printPdf(true),
            icon: 'document',
            show: () => this.hasAccessTo.print,
            disabled: () => this.synced !== 'synced',
            label: _('PRINT|Pobierz PDF'),
            tooltip: undefined,
            appearance: IccAppearance.Secondary,
            side: 'right',
            testId: 'download-pdf-button',
            children: [
                {
                    action: () => this.printPdf(),
                    icon: 'document',
                    show: () => this.hasAccessTo.print,
                    disabled: () => this.synced !== 'synced',
                    label: _('PRINT|Drukuj'),
                    tooltip: undefined,
                    appearance: IccAppearance.Secondary,
                    side: 'right',
                    testId: 'print-button',
                }
            ]
        },
        {
            action: () => this.printExternalVenetianOrderPdf(this.salesDocument),
            icon: 'document',
            show: () => this.hasExternalVenetian && this.hasAccessTo.printExternalVenetianOrderPdf,
            disabled: () => this.synced !== 'synced',
            label: _('EXTERNAL_VENETIAN|Drukuj zamówienie na żaluzje fasadowe'),
            tooltip: undefined,
            appearance: IccAppearance.Secondary,
            side: 'right',
            testId: 'print-external-venetian-order-pdf-button',
        },
        {
            action: () => this.goToLatestOrder(),
            icon: 'arrow_forward',
            show: () => this.showGoToOrderButton(),
            disabled: () => this.synced !== 'synced',
            label: _('SALES_DOCUMENT|Przejdź do zamówienia'),
            tooltip: undefined,
            appearance: IccAppearance.Primary,
            side: 'right',
            testId: 'go-to-order-button',
        },
        {
            action: () => this.goToLatestOrder(),
            icon: 'arrow_forward',
            show: () => this.showGoToCurrentOrderButton(),
            disabled: () => this.synced !== 'synced',
            label: _('SALES_DOCUMENT|Przejdź do aktualnego zamówienia'),
            tooltip: undefined,
            appearance: IccAppearance.Primary,
            side: 'right',
            testId: 'go-to-current-order-button',
        }
    ];

    RECALCULATION_CONFIRMATION_TOAST = 'recalculation-confirmation' as const;
    MODIFY_CONFIRMATION_TOAST = 'modify-confirmation' as const;

    cantManageOrderInfo: SafeHtml;
    @ViewChild('alert') alertElement: ElementRef<HTMLElement>;

    reportedMissingBuyDocument = false;

    constructor(
        private store: Store<State>,
        private salesDocumentService: SalesDocumentService,
        private router: Router,
        private priceVersionService: PriceVersionService,
        private http: HttpClient,
        private dialogService: DialogService,
        private configService: ConfigService,
        private exchangeRateService: ExchangeRateService,
        private translateService: TranslateService,
        private toastService: ToastService,
        private companyService: CompanyService,
        private permissionsService: NgxPermissionsService,
        private personsService: PersonsService,
        private dynamicElementsService: DynamicElementsService,
        private domSanitizer: DomSanitizer,
        private documentIntegrityService: DocumentIntegrityService,
        private offerValidityService: OfferValidityService,
        private syncService: SyncService,
        private documentValuationService: DocumentValuationService,
        private documentManagerService: DocumentManagerService,
        private documentCreationService: DocumentCreationService,
        private documentPricesService: DocumentPricesService,
        private b2bDocumentPricesService: B2bDocumentPricesService,
        private changeDetectorRef: ChangeDetectorRef,
        private ownProductsService: OwnProductsService,
        private configuratorLoaderService: ConfiguratorLoaderService,
        private positionService: PositionService,
        private breakpointObserver: BreakpointObserver,
    ) {
        this.subscriptions.push(
            this.priceVersionService.allPriceVersions$.pipe(
                map(data => data?.filter(item => item.hasAccess))
            ).subscribe((priceVersions) => {
                if (priceVersions) {
                    this.priceVersions = priceVersions ?? [];
                }
            })
        );
        this.subscriptions.push(
            this.router.events.subscribe((event: Event) => {
                if (event instanceof NavigationStart) {
                    this.toastService.clear(this.RECALCULATION_CONFIRMATION_TOAST);
                    this.toastService.clear(this.MODIFY_CONFIRMATION_TOAST);
                }
            }),
            this.priceVersionService.activatedPriceVersion$.subscribe(
                async (activePriceVersion) => {
                    this.actualPriceVersion = activePriceVersion;
                    this.invalidUnitsInDocument = false;
                    this.salesDocumentService.resetPositionBadUnits(this.openedDocument);
                    await this.handleDocumentAndActualPriceVersion(activePriceVersion);
                    if (activePriceVersion) {
                        this.loaded = true;
                    }
                }
            ),
            this.store.select(getSynced).subscribe(({synced}) => {
                this.syncedDocumentsState = synced;
                this.updatedSynced();
            }),
            this.permissionsService.permissions$.subscribe(() => {
                this.checkAccess();
                this.checkHasAccessToSale();
                this.checkHasAccessToBuy();
            }),
            this.breakpointObserver.observe(['(max-width: 990px)']).subscribe(result => {
                this.mobileView = result.matches;
            }),
        );

        this.tradePanelType = this.configService.config.config.Trade.tradePanelType;
    }

    async ngOnInit() {
        this.loadOwnProducts();
        this.loadCurrencies();
        this.loadExchangeRates();
        this.calcSummary();
        await this.handleDocumentAndActualPriceVersion(this.actualPriceVersion);
        this.setSalesDocumentSellerCompanyLicense();
        await this.dynamicElementsService.loaded$.toPromise();
        this.cantManageOrderInfo = this.domSanitizer.bypassSecurityTrustHtml(
            this.dynamicElementsService.getElementBySlug('sales-document-cant-manage-order')
                ?.content
        );
        this.latestOrderId = await this.findLatestOrderId();
    }

    async ngOnChanges() {
        if (this.openedDocument) {
            if (this.currentDocumentId !== this.openedDocument.uuid) {
                this.processing = false;
                this.currentDocumentId = this.openedDocument.uuid;
            }
            this.updatedSynced();
            this.checkIntegrity();
            await this.determineMode();
            await this.handleDocumentAndActualPriceVersion(this.actualPriceVersion);
            this.calcSummary();
            await this.checkAccess();
            this.checkHasAccessToSale();
            this.checkHasAccessToBuy();
            this.fixUnremovedPositions();
            this.loadStatus();
            this.latestOrderId = await this.findLatestOrderId();
        }
    }

    async onSellerChange() {
        await this.checkAccess();
        this.checkHasAccessToSale();
        this.checkHasAccessToBuy();
    }

    async onActiveBuyDocumentChange(document: SalesDocument) {
        this.activeBuyDocument = document;
        this.activeBuyDocumentChanged.next(document);
        this.calcSummary();
    }

    ngOnDestroy() {
        this.subscriptions.forEach((s) => s.unsubscribe());
    }

    get isGrossLabel() {
        return (
            ((this.mode === 'comparison' || this.mode === 'sale') && this.saleIsGross) ||
            (((this.isComparable && this.saleIsGross) || this.mode === 'purchase') &&
                this.buyIsGross)
        );
    }

    get saleIsGross() {
        return (
            this.salesDocument &&
            TaxRatesService.isGross(this.salesDocument.taxRateCalculationMethod)
        );
    }

    get buyIsGross() {
        return this.buyDocuments.every((document) =>
            TaxRatesService.isGross(document.taxRateCalculationMethod)
        );
    }

    get isComparable() {
        const salesTaxes = this.getSalesTaxRatesIds();
        return (
            this.mode === 'comparison' &&
            (!this.saleIsGross ||
                (this.buyIsGross &&
                    this.getBuyTaxRatesIds().every((tax) => salesTaxes.some((t) => t === tax))))
        );
    }

    get isPurchaseComparable() {
        const salesTaxes = this.getSalesTaxRatesIds();
        return (
            this.mode === 'comparison' &&
            (!this.saleIsGross ||
                (this.buyIsGross &&
                    !this.getBuyTaxRatesIds().every((tax) => salesTaxes.some((t) => t === tax))))
        );
    }

    get hasClassicConfigurator() {
        return this.configService.config.config.Configurators.active.includes('external_venetian');
    }

    updatedSynced() {
        this.openedDocumentsIds = [];

        if (this.salesDocument) {
            this.openedDocumentsIds.push(this.salesDocument.uuid);
            this.salesSynced = this.syncService.getSynced(
                [this.salesDocument.uuid],
                this.syncedDocumentsState
            );
        }
        if (this.buyDocuments?.length) {
            this.buyDocuments?.forEach((document) => {
                this.openedDocumentsIds.push(document.uuid);
            });
            this.buySynced = this.syncService.getSynced(
                this.buyDocuments.map((el) => el.uuid),
                this.syncedDocumentsState
            );
        }
        setTimeout(() => {
            this.synced = this.syncService.getSynced(
                this.openedDocumentsIds,
                this.syncedDocumentsState
            );
            console.log('%csynced', 'font-size: 16px; color: yellow', this.synced);
            this.changeDetectorRef.detectChanges();
        }, 0);

    }

    checkIntegrity() {
        let integral = true;
        const allDifferences: {
            salesDocument: SalesDocument['uuid'];
            buyDocument: SalesDocument['uuid'];
            salesPosition: SalesDocumentPosition['uuid'];
            buyPosition: SalesDocumentPosition['uuid'];
            differenceProp: DocumentDifference['prop'];
            differenceValue1: DocumentDifference['value1'];
            differenceValue2: DocumentDifference['value2'];
        }[] = [];
        if (this.salesDocument && this.buyDocuments.length > 0) {
            for (const buyDocument of this.buyDocuments) {
                const differences = DocumentIntegrityService.checkDocumentsIntegrity(
                    this.salesDocument,
                    buyDocument
                );
                allDifferences.push(
                    ...differences.map((el) => ({
                        salesDocument: this.salesDocument.uuid,
                        buyDocument: buyDocument.uuid,
                        salesPosition: null,
                        buyPosition: null,
                        differenceProp: el.prop,
                        differenceValue1: el.value1,
                        differenceValue2: el.value2,
                    }))
                );
                integral = integral && differences.length === 0;
            }
            const salesPositionsSet = new Set(this.positions.map((el) => el.uuid));
            for (const position of this.positions) {
                if (this.buyPositionsMap.has(position.uuid)) {
                    const positionDifferences = DocumentIntegrityService.checkPositionsIntegrity(
                        position,
                        this.buyPositionsMap.get(position.uuid),
                        this.buyPositionsMap,
                    );
                    allDifferences.push(
                        ...positionDifferences.map((el) => ({
                            salesDocument: this.salesDocument.uuid,
                            buyDocument: this.buyPositionsMap.get(position.uuid)?.documentId,
                            salesPosition: position.uuid,
                            buyPosition: this.buyPositionsMap.get(position.uuid)?.uuid,
                            differenceProp: el.prop,
                            differenceValue1: el.value1,
                            differenceValue2: el.value2,
                        }))
                    );
                    integral = integral && positionDifferences.length === 0;
                } else if (position.type !== 'additional') {
                    allDifferences.push({
                        salesDocument: this.salesDocument.uuid,
                        buyDocument: null,
                        salesPosition: position.uuid,
                        buyPosition: null,
                        differenceProp: null,
                        differenceValue1: null,
                        differenceValue2: null,
                    });
                    integral = false;
                }
            }
            const salesPositionsMap: Map<string, boolean> = new Map();
            this.buyPositionsMap.forEach((value, key) => {
                if (value && salesPositionsSet.has(key)) {
                    salesPositionsMap.set(value.uuid, true);
                }
            });
            for (const buyPosition of this.buyPositions) {
                if (!salesPositionsMap.has(buyPosition.uuid)) {
                    allDifferences.push({
                        salesDocument: this.salesDocument.uuid,
                        buyDocument: null,
                        salesPosition: null,
                        buyPosition: buyPosition.uuid,
                        differenceProp: null,
                        differenceValue1: null,
                        differenceValue2: null,
                    });
                    integral = false;
                }
            }
        }
        this.integral = integral;
        if (!integral) {
            console.table(allDifferences);
            Sentry.captureMessage(
                `Document integrity check failed`,
                {
                    extra: {
                        differences: allDifferences,
                        salesDocumentId: this.salesDocument?.uuid,
                        buyDocumentsIds: this.buyDocuments?.map((doc) => doc.uuid),
                        positionsCount: this.positions?.length,
                        buyPositionsCount: this.buyPositions?.length,
                    },
                }
            );
        }
    }

    async determineMode() {
        const state = history.state;
        if (
            (state &&
                state.documentType === this.openedDocument.salesDocumentTypeId &&
                state.documentType === SalesDocumentType.Order &&
                state.side === 'purchase' &&
                (await this.getHasAccessToBuy()) &&
                !this.modeInit) ||
            !this.salesDocument
        ) {
            this.switchMode('purchase');
            this.modeInit = true;
        } else if (
            this.salesDocument.salesDocumentTypeId === SalesDocumentType.Order &&
            !this.modeInit
        ) {
            this.switchMode('sale');
            this.modeInit = true;
        }

        if (!this.integral && this.mode === 'comparison') {
            this.switchMode('sale');
        }

        if (this.mode === 'purchase' && !this.buyDocuments?.length) {
            this.switchMode('sale');
        }
    }

    async checkHasAccessToSale() {
        this.hasAccessToSale = await this.getHasAccessToSale();
    }

    async checkHasAccessToBuy() {
        this.hasAccessToBuy = await this.getHasAccessToBuy();
    }

    async getHasAccessToSale() {
        if (!this.salesDocument) {
            return false;
        }

        return this.salesDocument.salesDocumentTypeId !== SalesDocumentType.Order
            ? await this.salesDocumentService.hasAccessToQuotationOrInquiry(this.salesDocument)
            : await this.salesDocumentService.hasAccessToOrder(this.salesDocument);
    }

    async getHasAccessToBuy() {
        const accessToBuyDocuments = [];

        if (!this.buyDocuments?.length) {
            return false;
        }

        for (const buyDocument of this.buyDocuments) {
            if (buyDocument.salesDocumentTypeId === SalesDocumentType.Order) {
                accessToBuyDocuments.push(
                    await this.salesDocumentService.hasAccessToOrder(buyDocument)
                );
            } else if (buyDocument.salesDocumentTypeId === SalesDocumentType.Inquiry) {
                accessToBuyDocuments.push(
                    await this.salesDocumentService.hasAccessToQuotationOrInquiry(buyDocument)
                );
            } else {
                accessToBuyDocuments.push(
                    true // TODO Co z ofertami zakupu - czy mają być zależne od uprawnienia Ceny zakupu?
                );
            }
        }
        return accessToBuyDocuments.every((el) => el);
    }

    async checkAccess() {
        if (!this.openedDocument) {
            return;
        }
        this.hasAccessTo = {
            addPosition: false,
            addOwnPosition: false,
            saleSide: false,
            comparisionSide: false,
            buySide: false,
            confirm: false,
            sendQuotationEmailToClient: false,
            sendToClient: false,
            recalculate: false,
            modify: false,
            modifySales: false,
            copy: false,
            order: false,
            placeOrder: false,
            print: false,
            printExternalVenetianOrderPdf: false,
            cancel: false,
            cancelOrder: false,
            delete: false,
            calculationInfo: false,
            cantModifyInfo: false,
            transferChanges: false,
            buyCurrency: false,
            inquireValuation: false,
            sendValuation: false,
            viewBuyPrices: false,
            consult: false,
            editCode: false,
            editRequestedDeadline: false,
            copySpareParts: false,
            deletePositionsBothSides: false,
        };
        await this.checkSaleAccess();
        await this.checkBuyAccess();

        this.hasExternalVenetian = this.positions.some((el) => el.type === 'external_venetian');
    }

    private async checkSaleAccess() {
        if (this.salesDocument) {
            let canManage = false;
            let canCancel = false;
            if (this.openedDocument.salesDocumentTypeId === SalesDocumentType.Order) {
                canManage = await this.salesDocumentService.hasAccessToOrder(
                    this.salesDocument,
                    'manage'
                );
                canCancel = await this.salesDocumentService.hasAccessToOrder(
                    this.salesDocument,
                    'cancel'
                );
            } else if (this.openedDocument.salesDocumentTypeId === SalesDocumentType.Inquiry) {
                canManage = await this.salesDocumentService.hasAccessToManageInquiry(
                    this.salesDocument
                );
                canCancel = await this.salesDocumentService.hasAccessToManageInquiry(
                    this.salesDocument
                );
            } else if (this.openedDocument.salesDocumentTypeId === SalesDocumentType.ServiceOrder) {
                canManage = await this.salesDocumentService.hasAccessToManageServiceOrder(
                    this.salesDocument
                );
                canCancel = await this.salesDocumentService.hasAccessToManageServiceOrder(
                    this.salesDocument
                );
            } else {
                canManage = await this.salesDocumentService.hasAccessToManageQuotation(
                    this.salesDocument
                );
                canCancel = await this.salesDocumentService.hasAccessToManageQuotation(
                    this.salesDocument
                );
            }

            const canBeEdited =
                !this.salesDocument.isCanceled &&
                !this.salesDocument.isInvalid &&
                !this.invalidOffer &&
                this.salesDocument.externalSource !== 'ERP';

            this.hasAccessTo.confirm =
                this.integral &&
                !this.salesDocument.isConfirmed &&
                this.salesDocument.salesDocumentTypeId !== SalesDocumentType.Inquiry &&
                this.salesDocument.salesDocumentTypeId !== SalesDocumentType.ServiceOrder &&
                canBeEdited &&
                this.positions.length > 0 &&
                canManage;
            this.hasAccessTo.transferChanges =
                !this.integral && canBeEdited && canManage;

            this.hasAccessTo.sendQuotationEmailToClient =
                this.salesDocument.isConfirmed &&
                canBeEdited &&
                this.salesDocument.salesDocumentTypeId !== SalesDocumentType.Order &&
                this.salesDocument.salesDocumentTypeId !== SalesDocumentType.Inquiry &&
                this.salesDocument.salesDocumentTypeId !== SalesDocumentType.ServiceOrder &&
                this.positions.length > 0 &&
                canManage;
            this.hasAccessTo.sendToClient =
                this.licenseType === 'license' &&
                !this.salesDocument.isCreatedBuyerDocument &&
                !this.salesDocument.isOrderPlaced &&
                this.salesDocument.client &&
                this.salesDocument.isConfirmed &&
                canBeEdited &&
                this.salesDocument.salesDocumentTypeId !== SalesDocumentType.Order &&
                this.salesDocument.salesDocumentTypeId !== SalesDocumentType.Inquiry &&
                this.salesDocument.salesDocumentTypeId !== SalesDocumentType.ServiceOrder &&
                this.positions.length > 0 &&
                canManage;

            this.hasAccessTo.recalculate =
                !this.salesDocument.isCanceled &&
                this.salesDocument.salesDocumentTypeId !== SalesDocumentType.ServiceOrder &&
                ((!this.salesDocument.isConfirmed &&
                    this.offerRecalculationMethod() === 'onlyEditedPosition') ||
                    (this.invalidOffer &&
                        this.salesDocument.salesDocumentTypeId !== SalesDocumentType.Order) ||
                    !this.salesDocument.isConfirmed) &&
                    this.positions.some((el) => el.externalSource !== 'ERP' && (el.type === 'additional' || !this.salesDocument.isConfirmedOnlySalesPositions)) &&
                canManage;
            this.hasAccessTo.modify =
                this.salesDocument.isConfirmed &&
                this.salesDocument.salesDocumentTypeId !== SalesDocumentType.ServiceOrder &&
                (canBeEdited ||
                    (this.invalidOffer &&
                        this.salesDocument.salesDocumentTypeId === SalesDocumentType.Order)) &&
                canManage &&
                !this.salesDocument.isAnnexed;
            this.hasAccessTo.modifySales =
                this.licenseType !== 'license' &&
                this.integral &&
                this.salesDocument.salesDocumentTypeId === SalesDocumentType.Order &&
                this.salesDocument.isConfirmed &&
                (!this.buyDocuments?.length || this.buyDocuments.every((el) => el.isOrderPlaced || !el.isConfirmed)) &&
                !this.salesDocument.isAnnexed &&
                (canBeEdited ||
                    (this.invalidOffer &&
                        this.salesDocument.salesDocumentTypeId === SalesDocumentType.Order)) &&
                canManage;
            this.hasAccessTo.copy =
                !this.salesDocument.isCanceled &&
                this.salesDocument.salesDocumentTypeId !== SalesDocumentType.Order &&
                this.salesDocument.salesDocumentTypeId !== SalesDocumentType.Inquiry &&
                this.integral &&
                canManage;
            this.hasAccessTo.copySpareParts =
                !this.salesDocument.isCanceled &&
                this.salesDocument.salesDocumentTypeId !== SalesDocumentType.Inquiry &&
                await this.canBeCreatedSparePartsDocuments() &&
                canManage;
            this.hasAccessTo.order =
                (this.salesDocument.isConfirmed || this.salesDocument.salesDocumentTypeId === SalesDocumentType.ServiceOrder) &&
                !this.salesDocument.isOrderPlaced &&
                !this.salesDocument.isAnnexed &&
                this.salesDocument.salesDocumentTypeId !== SalesDocumentType.Order &&
                this.salesDocument.salesDocumentTypeId !== SalesDocumentType.Inquiry &&
                !this.salesDocument.hasNonValuatedPositions &&
                (this.salesDocument.buyerCompanyId != null || this.salesDocument.salesDocumentTypeId !== SalesDocumentType.ServiceOrder) &&
                this.integral &&
                canBeEdited &&
                canManage;
            this.hasAccessTo.placeOrder =
                canManage &&
                !this.salesDocument.isOrderPlaced &&
                !this.salesDocument.isAnnexed &&
                (this.salesDocument.isConfirmed || this.salesDocument.salesDocumentTypeId === SalesDocumentType.ServiceOrder) &&
                !this.invalidOffer &&
                !this.salesDocument.isCanceled &&
                !this.salesDocument.hasNonValuatedPositions &&
                this.salesDocument.externalSource !== 'ERP' &&
                !this.hasAccessTo.confirm &&
                (this.openedDocument.salesDocumentTypeId === SalesDocumentType.Order || this.salesDocument.salesDocumentTypeId === SalesDocumentType.ServiceOrder) &&
                this.mode === 'sale' &&
                this.buyDocuments.length === 0;

            this.hasAccessTo.delete =
                !this.salesDocument.isOrderPlaced &&
                (this.salesDocument.sellerCompanyId === this.salesDocument.author.companyId ||
                    this.configService.user.user.ActiveCompanyLicenseType === 'sublicense') &&
                this.salesDocument.salesDocumentTypeId !== SalesDocumentType.Order &&
                this.salesDocument.salesDocumentTypeId !== SalesDocumentType.Inquiry &&
                this.salesDocument.salesDocumentTypeId !== SalesDocumentType.ServiceOrder &&
                this.salesDocument.externalSource !== 'ERP' &&
                canManage;
            this.hasAccessTo.cancel =
                !this.salesDocument.isOrderPlaced &&
                !this.salesDocument.isAnnexed &&
                !this.salesDocument.isConfirmed &&
                this.salesDocument.salesDocumentTypeId !== SalesDocumentType.Order &&
                this.salesDocument.salesDocumentTypeId !== SalesDocumentType.ServiceOrder &&
                canBeEdited &&
                canCancel;
            this.hasAccessTo.cancelOrder =
                (
                    this.salesDocument.salesDocumentTypeId === SalesDocumentType.Order ||
                    this.salesDocument.salesDocumentTypeId === SalesDocumentType.ServiceOrder
                ) &&
                !this.salesDocument.isAnnexed &&
                !this.salesDocument.isDelivered &&
                this.integral &&
                canBeEdited &&
                canCancel;

            this.hasAccessTo.sendValuation =
                this.salesDocument.salesDocumentTypeId === SalesDocumentType.Inquiry &&
                !this.salesDocument.isConfirmed &&
                canBeEdited &&
                this.positions.length > 0 &&
                this.salesDocument.priceBeforeDiscountsValue > 0 &&
                canManage;

            this.hasAccessTo.editCode =
                !this.salesDocument.isConfirmed &&
                this.salesDocument.salesDocumentTypeId !== SalesDocumentType.Order &&
                this.salesDocument.salesDocumentTypeId !== SalesDocumentType.Inquiry &&
                canBeEdited &&
                canManage;

            this.hasAccessTo.editRequestedDeadline =
                !this.salesDocument.isConfirmed &&
                this.salesDocument.salesDocumentTypeId !== SalesDocumentType.Order &&
                this.salesDocument.salesDocumentTypeId !== SalesDocumentType.Inquiry &&
                canBeEdited &&
                await this.permissionsService.hasPermission(['sell-requested-deadline-manage']) &&
                canManage;

            this.hasAccessTo.print = this.salesDocument.salesDocumentTypeId !== SalesDocumentType.Inquiry && this.salesDocument.salesDocumentTypeId !== SalesDocumentType.ServiceOrder;
            this.hasAccessTo.printExternalVenetianOrderPdf = this.salesDocument.salesDocumentTypeId !== SalesDocumentType.Inquiry;

            this.hasAccessTo.addPosition = await this.salesDocumentService.canAddPositionToQuotation(this.salesDocument);
            this.hasAccessTo.addOwnPosition = await this.salesDocumentService.canAddPositionToQuotation(this.salesDocument, true);

            this.hasAccessTo.deletePositionsBothSides = !this.salesDocument.isConfirmed &&
                this.salesDocument.salesDocumentTypeId !== SalesDocumentType.Inquiry &&
                !this.salesDocument.isOrderPlaced &&
                !this.salesDocument.isAnnexed &&
                canBeEdited &&
                canManage;

        } else {
            if (this.openedDocument?.salesDocumentTypeId !== SalesDocumentType.Order) {
                this.hasAccessTo.confirm = false;
            }
            this.hasAccessTo.transferChanges = false;
            this.hasAccessTo.sendQuotationEmailToClient = false;
            this.hasAccessTo.sendToClient = false;
            this.hasAccessTo.recalculate = false;
            this.hasAccessTo.modify = false;
            this.hasAccessTo.modifySales = false;
            this.hasAccessTo.copy = false;
            this.hasAccessTo.order = false;
            this.hasAccessTo.delete = false;
            this.hasAccessTo.cancel = false;
            this.hasAccessTo.cancelOrder = false;
            this.hasAccessTo.sendValuation = false;
            this.hasAccessTo.editCode = false;
            this.hasAccessTo.editRequestedDeadline = false;
            this.hasAccessTo.deletePositionsBothSides = false;
        }
    }

    private async checkBuyAccess() {
        const shouldHaveBuyDocument = this.positions.some(
            (el) => el.type !== 'additional'
        ) && this.licenseType !== 'license';
        if (this.buyDocuments?.length) {
            let canManageBuySide = false;
            let canCancelBuySide = false;
            if (this.openedDocument.salesDocumentTypeId === SalesDocumentType.Order) {
                const accessToManageBuyDocuments = [];
                const accessToCancelBuyDocuments = [];
                for (const buyDocument of this.buyDocuments) {
                    accessToManageBuyDocuments.push(
                        await this.salesDocumentService.hasAccessToOrder(buyDocument, 'manage')
                    );
                    accessToCancelBuyDocuments.push(
                        await this.salesDocumentService.hasAccessToOrder(buyDocument, 'cancel')
                    );
                }
                canManageBuySide =
                    this.buyDocuments.length === 0 || accessToManageBuyDocuments.every((el) => el);
                canCancelBuySide =
                    this.buyDocuments.length === 0 || accessToCancelBuyDocuments.every((el) => el);
            } else if (this.openedDocument.salesDocumentTypeId === SalesDocumentType.ServiceOrder) {
                canManageBuySide = await this.salesDocumentService.hasAccessToManageServiceOrder(this.openedDocument);
                canCancelBuySide = await this.salesDocumentService.hasAccessToManageServiceOrder(this.openedDocument);
            } else {
                canManageBuySide = true;
                canCancelBuySide = true;
            }

            let canBeEditedBuyDocument = true;
            for (const buyDocument of this.buyDocuments) {
                const { invalidOffer } = this.offerValidityService.getValidDate(buyDocument);
                canBeEditedBuyDocument =
                    canBeEditedBuyDocument &&
                    !buyDocument.isCanceled &&
                    !buyDocument.isInvalid &&
                    !invalidOffer &&
                    buyDocument.externalSource !== 'ERP' &&
                    !buyDocument.isConfirmed;

                let canManage = false;
                if (buyDocument.salesDocumentTypeId === SalesDocumentType.ServiceOrder) {
                    canManage = await this.salesDocumentService.hasAccessToManageServiceOrder(buyDocument);
                } else {
                    canManage = await this.salesDocumentService.hasAccessToOrder(
                        buyDocument,
                        'manage'
                    );
                }

                this.hasAccessTo.placeOrder =
                    this.hasAccessTo.placeOrder ||
                    (canManage &&
                        !buyDocument.isOrderPlaced &&
                        !buyDocument.isAnnexed &&
                        (buyDocument.isConfirmed || buyDocument.salesDocumentTypeId === SalesDocumentType.ServiceOrder) &&
                        !invalidOffer &&
                        !buyDocument.isCanceled &&
                        !buyDocument.hasNonValuatedPositions &&
                        buyDocument.externalSource !== 'ERP' &&
                        (buyDocument.buyerCompanyId != null || buyDocument.salesDocumentTypeId !== SalesDocumentType.ServiceOrder));

                this.hasAccessTo.inquireValuation =
                    this.hasAccessTo.inquireValuation ||
                    (!buyDocument.isOrderPlaced &&
                        !buyDocument.isCanceled &&
                        buyDocument.hasNonValuatedPositions);

                this.hasAccessTo.consult =
                    this.hasAccessTo.consult ||
                    (buyDocument.isConfirmed &&
                        !buyDocument.isAnnexed &&
                        !buyDocument.isOrderPlaced &&
                        !buyDocument.isCanceled &&
                        !buyDocument.hasNonValuatedPositions &&
                        buyDocument.externalSource !== 'ERP'
                    );

                this.hasAccessTo.modify = this.hasAccessTo.modify &&
                    buyDocument.isConfirmed &&
                    !buyDocument.isAnnexed &&
                    !buyDocument.isCanceled &&
                    !buyDocument.isInvalid &&
                    !invalidOffer &&
                    buyDocument.externalSource !== 'ERP' &&
                    (buyDocument.isOrderPlaced || !buyDocument.isOrderPlaced && buyDocument.authorCompanyId === Number(this.configService?.user?.user?.ActiveCompany));

                this.hasAccessTo.transferChanges = this.hasAccessTo.transferChanges && (
                    buyDocument.salesDocumentTypeId !== SalesDocumentType.Order ||
                    buyDocument.isConfirmed
                );

                this.hasAccessTo.editCode = this.hasAccessTo.editCode &&
                    !buyDocument.isConfirmed &&
                    buyDocument.salesDocumentTypeId !== SalesDocumentType.Order &&
                    buyDocument.salesDocumentTypeId !== SalesDocumentType.Inquiry &&
                    canBeEditedBuyDocument &&
                    canManageBuySide;

                this.hasAccessTo.cancelOrder = canCancelBuySide &&
                    (this.hasAccessTo.cancelOrder || !buyDocument.isCanceled) &&
                    !buyDocument.isDelivered &&
                    (!buyDocument.isOrderPlaced || buyDocument.salesDocumentTypeId !== SalesDocumentType.ServiceOrder) &&
                    (buyDocument.salesDocumentTypeId === SalesDocumentType.Order || buyDocument.salesDocumentTypeId === SalesDocumentType.ServiceOrder);

                this.hasAccessTo.print = this.hasAccessTo.print || buyDocument.salesDocumentTypeId !== SalesDocumentType.Inquiry && buyDocument.salesDocumentTypeId !== SalesDocumentType.ServiceOrder;
                this.hasAccessTo.printExternalVenetianOrderPdf = this.hasAccessTo.printExternalVenetianOrderPdf || buyDocument.salesDocumentTypeId !== SalesDocumentType.Inquiry;

                this.hasAccessTo.deletePositionsBothSides = this.hasAccessTo.deletePositionsBothSides &&
                    !buyDocument.isConfirmed &&
                    buyDocument.salesDocumentTypeId !== SalesDocumentType.Inquiry &&
                    !buyDocument.isOrderPlaced &&
                    !buyDocument.isAnnexed &&
                    canBeEditedBuyDocument &&
                    canManageBuySide;
            }

            this.hasAccessTo.modify =
                this.hasAccessTo.modify &&
                (this.openedDocument.salesDocumentTypeId !== SalesDocumentType.Order ||
                    canManageBuySide);

            this.hasAccessTo.cancelOrder = this.hasAccessTo.cancelOrder && canCancelBuySide;

            if (this.openedDocument.salesDocumentTypeId === SalesDocumentType.ServiceOrder) {
                this.hasAccessTo.placeOrder = this.hasAccessTo.placeOrder && canManageBuySide && this.positions.length > 0;
            } else {
                this.hasAccessTo.placeOrder =
                    !this.hasAccessTo.confirm &&
                    this.hasAccessTo.placeOrder &&
                    this.openedDocument.salesDocumentTypeId === SalesDocumentType.Order &&
                    this.mode !== 'sale' &&
                    (await this.permissionsService.hasPermission(['purchase-make-order']));
            }

            this.hasAccessTo.inquireValuation =
                this.hasAccessTo.inquireValuation &&
                this.openedDocument.salesDocumentTypeId === SalesDocumentType.Quotation &&
                (await this.permissionsService.hasPermission(['purchase-inquiries-manage']));

            this.hasAccessTo.consult =
                this.hasAccessTo.consult &&
                this.openedDocument.salesDocumentTypeId === SalesDocumentType.Quotation &&
                (await this.permissionsService.hasPermission(['purchase-consult']));

            this.hasAccessTo.cantModifyInfo =
                !canManageBuySide &&
                this.openedDocument.isConfirmed &&
                this.openedDocument.salesDocumentTypeId === SalesDocumentType.Order &&
                !this.openedDocument.isCanceled;
            this.hasAccessTo.buyCurrency =
                canManageBuySide &&
                canBeEditedBuyDocument &&
                this.buyDocuments.some(
                    (buyDocument) =>
                        !buyDocument.isConfirmed &&
                        buyDocument.salesDocumentTypeId === SalesDocumentType.Quotation &&
                        !buyDocument.isCanceled
                );
            this.hasAccessTo.viewBuyPrices = (await this.permissionsService.hasPermission(['purchase-prices-view']));

            this.hasAccessTo.addPosition = this.hasAccessTo.addPosition || await this.salesDocumentService.canAddPositionToQuotation(this.openedDocument) &&
                canManageBuySide &&
                canBeEditedBuyDocument &&
                this.openedDocument.salesDocumentTypeId === SalesDocumentType.ServiceOrder;
        } else {
            this.hasAccessTo.cantModifyInfo = false;
            this.hasAccessTo.buyCurrency = false;
            this.hasAccessTo.placeOrder = false;
            if (this.openedDocument.salesDocumentTypeId !== SalesDocumentType.ServiceOrder) {
                this.hasAccessTo.cancelOrder = false;
            }
            this.hasAccessTo.viewBuyPrices = false;
            this.hasAccessTo.inquireValuation = false;
            this.hasAccessTo.consult = false;

            if (shouldHaveBuyDocument) {
                this.hasAccessTo.modify = false;
                this.hasAccessTo.transferChanges = false;
                this.hasAccessTo.editCode = false;
                this.hasAccessTo.print = false;
                this.hasAccessTo.printExternalVenetianOrderPdf = false;
                this.hasAccessTo.deletePositionsBothSides = false;
                this.hasAccessTo.addPosition = false;
                this.hasAccessTo.recalculate = false;
                this.hasAccessTo.sendValuation = false;
                this.hasAccessTo.copy = false;
                this.hasAccessTo.order = false;
                this.hasAccessTo.placeOrder = false;
                this.hasAccessTo.delete = false;

                if (!this.reportedMissingBuyDocument) {
                    logger.error(
                        'Brak dokumentów zakupu, ale powinny być. Sprawdź, czy są jakieś pozycje, które wymagają dokumentu zakupu.'
                    );
                    Sentry.captureMessage(
                        'Brak dokumentów zakupu, ale powinny być. Sprawdź, czy są jakieś pozycje, które wymagają dokumentu zakupu.',
                        'error'
                    );
                    this.reportedMissingBuyDocument = true;
                    this.getBuyDocumentsToSalesDocument(this.openedDocument);
                }
            }
        }
        this.hasExternalVenetian = this.positions.some((el) => el.type === 'external_venetian');
    }

    async getBuyDocumentsToSalesDocument(document) {
        const buyDocuments = await this.http.get<string[]>(`${window.location.origin}/api/b2b/linked-buy-documents/${document.uuid}`).toPromise();
        if (buyDocuments && buyDocuments.length) {
            for (const buyDocumentId of buyDocuments) {
                await this.syncService.addCurrentDocumentId(buyDocumentId);
            }
            await this.syncService.waitForSynced(buyDocuments);
            await this.syncService.loadDocuments(buyDocuments);
        }
    }

    hasAccessToAny(actions: DocumentAction[]) {
        return actions.some(action => action.show());
    }

    handleValidityDate() {
        const {
            showValidityInfo,
            showOfferValidityWarning,
            invalidOffer,
            visibleValidDate,
        } = this.offerValidityService.getValidDate(this.openedDocument);

        this.showValidityInfo = showValidityInfo;
        this.visibleValidDate = visibleValidDate;
        this.showOfferValidityWarning = showOfferValidityWarning;
        this.invalidOffer = invalidOffer;
        this.checkAccess();
    }

    switchMode(mode: SalesDocumentViewMode) {
        this.mode = mode;
        this.modeChanged.emit(mode);
        if (mode === 'sale') {
            this.activeBuyDocument = null;
            this.onActiveBuyDocumentChange(null);
        }
        this.checkAccess();
        this.calcSummary();
    }

    backToList() {
        const state = history.state;
        const commands = ['trade', 'offer', 'list'];
        if (state && state.documentType === this.openedDocument.salesDocumentTypeId) {
            if (state.documentType === SalesDocumentType.Quotation) {
                commands.push('offers');
            } else if (state.documentType === SalesDocumentType.Inquiry) {
                commands.push('inquiries');
                if (state.side === 'sales') {
                    commands.push('sales');
                } else {
                    commands.push('purchase');
                }
            } else if (state.documentType === SalesDocumentType.ServiceOrder) {
                commands.push('service-orders');
            } else {
                commands.push('orders');
                if (state.side === 'sales') {
                    commands.push('sales');
                } else if (state.unplaced) {
                    commands.push('unplaced');
                } else {
                    commands.push('purchase');
                }
            }
        } else {
            if (this.openedDocument.salesDocumentTypeId === SalesDocumentType.Quotation) {
                commands.push('offers');
            } else if (this.openedDocument.salesDocumentTypeId === SalesDocumentType.Inquiry) {
                commands.push('inquiries', 'sales');
            } else if (state.documentType === SalesDocumentType.ServiceOrder) {
                commands.push('service-orders');
            }  else {
                commands.push('orders', 'sales');
            }
        }
        this.router.navigate(commands);
    }

    getBtnBackText() {
        if (this.mobileView) {
            return this.translateService.instant('INTERFACE|Powrót do listy');
        }
        const state = history.state;
        if (state && state.documentType === this.openedDocument.salesDocumentTypeId) {
            if (state.documentType === SalesDocumentType.Quotation) {
                return this.translateService.instant('SALES_DOCUMENT|Powrót do listy ofert');
            } else if (state.documentType === SalesDocumentType.Inquiry) {
                if (state.side === 'sales') {
                    return this.translateService.instant(
                        'SALES_DOCUMENT|Powrót do listy zapyt. klientów'
                    );
                } else {
                    return this.translateService.instant(
                        'SALES_DOCUMENT|Powrót do listy zapyt. u dostawcy'
                    );
                }
            } else if (state.documentType === SalesDocumentType.ServiceOrder) {
                return this.translateService.instant(
                    'SALES_DOCUMENT|Powrót do listy zam. serwisowych'
                );
            } else {
                if (state.side === 'sales') {
                    return this.translateService.instant(
                        'SALES_DOCUMENT|Powrót do listy zam. klientów'
                    );
                } else if (state.unplaced) {
                    return this.translateService.instant(
                        'SALES_DOCUMENT|Powrót do listy zam. u dostawcy do potwierdzenia'
                    );
                } else {
                    return this.translateService.instant(
                        'SALES_DOCUMENT|Powrót do listy zam. u dostawcy'
                    );
                }
            }
        } else {
            if (this.openedDocument.salesDocumentTypeId === SalesDocumentType.Quotation) {
                return this.translateService.instant('SALES_DOCUMENT|Powrót do listy ofert');
            } else if (this.openedDocument.salesDocumentTypeId === SalesDocumentType.Inquiry) {
                return this.translateService.instant('SALES_DOCUMENT|Powrót do listy zapyt. klientów');
            } else if (state.documentType === SalesDocumentType.ServiceOrder) {
                return this.translateService.instant(
                    'SALES_DOCUMENT|Powrót do listy zam. serwisowych'
                );
            } else {
                return this.translateService.instant(
                    'SALES_DOCUMENT|Powrót do listy zam. klientów'
                );
            }
        }
    }

    async confirmConfirm(document: SalesDocument) {
        if (document.salesDocumentTypeId === SalesDocumentType.Order) {
            if (this.licenseType === 'license') {
                const result = await this.dialogService
                    .open(ConfirmOrderDialogComponent, {
                        data: {
                            document,
                            buyDocuments: this.buyDocuments,
                        },
                    })
                    .afterClosed()
                    .toPromise();
                if (result) {
                    this.confirmOrderDocument(document, result.deliveryDate, result.confirmLater, result.confirmLater);
                }
            } else {
                const possible: SalesDocument[] = await this.getPossibleOrdersToPlace(this.buyDocuments, true);
                if (possible.length) {
                    this.confirmOrder(document, document.uuid);
                } else {
                    this.toastService.add({
                        title: this.translateService.instant('SALES_DOCUMENT|Zatwierdzanie dokumentu'),
                        body: this.translateService.instant('SALES_DOCUMENT|Czy na pewno zatwierdzić i oznaczyć dokument jako zamówiony?'),
                        severity: 'question',
                        sticky: true,
                        buttons: [
                            {
                                title: this.translateService.instant('INTERFACE|Tak'),
                                onClick: () => this.confirmOrderDocument(document, null, false, true),
                            },
                            {
                                title: this.translateService.instant('INTERFACE|Nie'),
                                onClick: () => null,
                            },
                        ],
                    });
                }
            }
        } else {
            this.confirm(document);
        }
    }

    confirmOrderDocument(document: SalesDocument, deliveryDate: string = null, confirmLaterSales = false, confirmLaterBuy = false, placeBuyOrder = false) {
        return this.salesDocumentService.confirmOrder(document, deliveryDate, confirmLaterSales, confirmLaterBuy, placeBuyOrder);
    }

    confirm(document: SalesDocument) {
        if (!this.invalidOffer) {
            this.salesDocumentService.confirm(document);
        }
    }

    async sendQuotationEmailToClient(document: SalesDocument, sendEmail = true) {
        if (document.client?.email || !sendEmail) {
            this.documentManagerService.saveDocument(document.uuid, {
                isSentToBuyer: true,
            });
            let status;
            if (sendEmail) {
                status = await this.http
                    .get<{ status: string }>(
                        `${window.location.origin}/api/b2b/send-offer-email/${document.uuid}`
                    )
                    .toPromise();
            }
            if (!sendEmail) {
                this.toastService.add({
                    title: this.translateService.instant('SALES_DOCUMENT|Oferta została przekazana do klienta'),
                    body: this.translateService.instant(
                        'SALES_DOCUMENT|Oferta nr {offerKey} została przekazana do klienta',
                        { offerKey: document.documentNumber.join('') }
                    ),
                });
            } else if (status?.status === 'ok') {
                this.toastService.add({
                    title: this.translateService.instant('SALES_DOCUMENT|Oferta została wysłana'),
                    body: this.translateService.instant(
                        'SALES_DOCUMENT|Została wysłana oferta nr {offerKey}',
                        { offerKey: document.documentNumber.join('') }
                    ),
                });
            } else {
                this.toastService.add({
                    title: this.translateService.instant(
                        'SALES_DOCUMENT|Oferta nie została wysłana'
                    ),
                    body: this.translateService.instant(
                        'SALES_DOCUMENT|Wystąpił błąd podczas wysyłania oferty nr {offerKey}',
                        { offerKey: document.documentNumber.join('') }
                    ),
                });
            }
        } else {
            this.toastService.add({
                title: this.translateService.instant('SALES_DOCUMENT|Brak adresu e-mail klienta'),
                body: this.translateService.instant(
                    'SALES_DOCUMENT|Brak emaila klienta dla oferty nr {offerKey}',
                    { offerKey: document.documentNumber.join('') }
                ),
                severity: 'error',
            });
        }
    }

    confirmSendToContractor(document: SalesDocument) {
        if (!document.buyerContractorId && !document.client?.email ) {
            this.toastService.add({
                title: this.translateService.instant('SALES_DOCUMENT|Brak adresu e-mail klienta'),
                body: this.translateService.instant(
                    'SALES_DOCUMENT|Brak emaila klienta dla oferty nr {offerKey}',
                    { offerKey: document.documentNumber.join('') }
                ),
                severity: 'error',
            });
        } else {
            const title = this.translateService.instant(
                'SALES_DOCUMENT|Potwierdzenie wysłania do klienta'
            );
            const body = this.translateService.instant(
                'SALES_DOCUMENT|Czy chcesz wysłać ofertę do klienta? Po wysłaniu nie będzie możliwości wprowadzania zmian na ofercie oraz zmiany klienta.'
            );
            this.toastService.add({
                title,
                body,
                severity: 'question',
                sticky: true,
                buttons: [
                    {
                        title: this.translateService.instant('INTERFACE|Tak'),
                        onClick: () => this.sendToContractor(document),
                    },
                    {
                        title: this.translateService.instant('INTERFACE|Nie'),
                        onClick: () => null,
                    },
                ],
            });
        }
    }

    async sendToContractor(document: SalesDocument) {
        this.toastService.add({
            title: this.translateService.instant('SALES_DOCUMENT|Wysyłanie oferty do klienta'),
            body: this.translateService.instant(
                'SALES_DOCUMENT|Oferta {offerKey} jest wysyłana do klienta',
                { offerKey: document.documentNumber.join('') }
            ),
        });
        document.isCreatedBuyerDocument = true;
        this.documentManagerService.saveDocument(document.uuid, {
            isSentToBuyer: true,
            isCreatedBuyerDocument: true,
        });
        try {
            const status = await this.http
                .get<{ status: string }>(
                    `${window.location.origin}/api/b2b/send-to-contractor/${document.uuid}`
                )
                .toPromise();
            if (status?.status === 'ok') {
                this.toastService.add({
                    title: this.translateService.instant('SALES_DOCUMENT|Oferta została wysłana'),
                    body: this.translateService.instant(
                        'SALES_DOCUMENT|Oferta nr {offerKey} została wysłana do klienta',
                        { offerKey: document.documentNumber.join('') }
                    ),
                });
            } else {
                document.isCreatedBuyerDocument = false;
                this.documentManagerService.saveDocument(document.uuid, {
                    isSentToBuyer: false,
                    isCreatedBuyerDocument: false,
                });
                this.toastService.add({
                    title: this.translateService.instant(
                        'SALES_DOCUMENT|Oferta nie została wysłana do klienta'
                    ),
                    body: this.translateService.instant(
                        'SALES_DOCUMENT|Wystąpił błąd podczas wysyłania oferty nr {offerKey}',
                        { offerKey: document.documentNumber.join('') }
                    ),
                });
            }
        } catch (error) {
            logger.error(error);
            this.documentManagerService.saveDocument(document.uuid, {
                isSentToBuyer: false,
                isCreatedBuyerDocument: false,
            });
            this.toastService.add({
                title: this.translateService.instant(
                    'SALES_DOCUMENT|Oferta nie została wysłana do klienta'
                ),
                body: this.translateService.instant(
                    'SALES_DOCUMENT|Wystąpił błąd podczas wysyłania oferty nr {offerKey}',
                    { offerKey: document.documentNumber.join('') }
                ),
            });
        }
    }

    async confirmCancel(document: SalesDocument) {
        const title = this.translateService.instant('SALES_DOCUMENT|Potwierdzenie anulowania');
        let body = this.translateService.instant(
            'SALES_DOCUMENT|Czy chcesz anulować ten dokument?'
        );
        if (document.salesDocumentTypeId === SalesDocumentType.Order) {
            if (this.buyDocuments.length === 0) {
                body = this.translateService.instant(
                    'SALES_DOCUMENT|Czy na pewno chcesz anulować zamówienie sprzedaży nr {salesOrderNo}? Czynności nie można cofnąć.',
                    {
                        salesOrderNo: document.documentNumber?.join('') || '',
                    }
                );
            } else if (this.buyDocuments.length === 1) {
                body = this.translateService.instant(
                    'SALES_DOCUMENT|Czy na pewno chcesz anulować zamówienie sprzedaży nr {salesOrderNo} oraz zamówienie zakupu nr {buyOrderNo}? Czynności nie można cofnąć.',
                    {
                        salesOrderNo: document.documentNumber?.join('') || '',
                        buyOrderNo: this.buyDocuments[0].documentNumber?.join('') || '',
                    }
                );
            } else {
                body = this.translateService.instant(
                    'SALES_DOCUMENT|Czy na pewno chcesz anulować zamówienie sprzedaży nr {salesOrderNo} oraz wszystkie powiązane zamówienia zakupu? Czynności nie można cofnąć.',
                    {
                        salesOrderNo: document.documentNumber?.join('') || '',
                    }
                );
            }
        }

        if (document.salesDocumentTypeId === SalesDocumentType.Order &&
            this.configService.user.orderIntegration === 'OrdersSynchronization'
        ) {
            const result = await lastValueFrom(this.dialogService
                .open(CancelOrderDialogComponent, {
                    data: {
                        document,
                        buyDocuments: this.buyDocuments.filter((doc) => !doc.isEmpty),
                        body,
                    },
                })
                .afterClosed());
            if (result?.status) {
                this.cancel(document);
            }
        } else {
            this.toastService.add({
                title,
                body,
                severity: 'question',
                sticky: true,
                buttons: [
                    {
                        title: this.translateService.instant('INTERFACE|Tak'),
                        onClick: () => this.cancel(document),
                    },
                    {
                        title: this.translateService.instant('INTERFACE|Nie'),
                        onClick: () => null,
                    },
                ],
            });
        }
    }

    cancel(document: SalesDocument) {
        this.salesDocumentService.cancel(document);
    }

    async handleDocumentAndActualPriceVersion(actualPriceVersion: PriceVersion) {
        if (this.salesDocument) {
            if (!this.salesDocument?.isConfirmed &&
                !this.salesDocument.isConfirmedOnlySalesPositions &&
                !this.salesDocument?.isCanceled &&
                this.salesDocument.salesDocumentTypeId !== SalesDocumentType.Inquiry
            ) {
                if (this.salesDocument?.priceVersionId && actualPriceVersion) {
                    this.priceVersionObj = this.priceVersionService
                        .getPriceVersion(this.salesDocument?.priceVersionId);
                    if (this.salesDocument?.priceVersionId === actualPriceVersion?.id) {
                        this.handleValidityDate();
                        this.invalidVersion = false;

                        this.invalidVersionEffectiveDate = this.priceVersionObj?.effectiveDate && (new Date() < new Date(this.priceVersionObj.effectiveDate));
                        this.invalidVersionExpiredDate = this.priceVersionObj?.expiredDate && (new Date() > new Date(this.priceVersionObj.expiredDate));
                        return;
                    } else if (this.priceVersionObj?.hasAccess && this.changingActivePriceVersion !== this.salesDocument?.uuid) {
                        this.changingActivePriceVersion = this.salesDocument?.uuid;
                        this.priceVersionService.activatePriceVersionData(this.salesDocument?.priceVersionId).then(() => {
                            this.changingActivePriceVersion = null;
                        });
                    }
                }
                this.handleValidityDate();
                this.invalidVersion = true;
            } else {
                this.invalidVersion = false;
                this.priceVersionObj = this.priceVersionService
                    .getPriceVersion(this.salesDocument?.priceVersionId);
                this.handleValidityDate();
            }

            this.invalidVersionEffectiveDate = this.priceVersionObj?.effectiveDate && (new Date() < new Date(this.priceVersionObj.effectiveDate));
            this.invalidVersionExpiredDate = this.priceVersionObj?.expiredDate && (new Date() > new Date(this.priceVersionObj.expiredDate));
        }
    }

    // eslint-disable-next-line max-statements
    calcSummary() {
        this.summary = {
            sales: null,
            buySales: null,
            buy: null,
            taxes: [],
            groups: [],
            groupsPositions: [],
            result: {
                value: 0,
                margin: 0,
                overhead: 0,
            },
        };

        if (this.salesDocument) {
            this.summary.sales = DocumentSummarize.summarize(this.salesDocument, this.positions);
            this.summary.sales.taxes.forEach((tax) => {
                this.summary.taxes.push({
                    taxRate: tax.taxRate,
                    sales: tax,
                    buySales: null,
                    buy: null,
                });
            });

            const salesGrouping = this.summary.sales.groupings.find(
                (el) => el.groupSchemaId === +this.summingGroupingSchemaId
            );
            if (salesGrouping) {
                salesGrouping.groups.forEach((group) => {
                    this.summary.groups.push({
                        key: group.key,
                        name: group.name,
                        sales: group,
                        buySales: null,
                        buy: null,
                    });
                });
            }

            this.summary.sales.groupsPositions.forEach((gp) => {
                this.summary.groupsPositions.push({
                    groupSchemaId: gp.groupSchemaId,
                    name: gp.name,
                    sales: gp,
                    buySales: null,
                    buy: null,
                });
            });
        }

        const buyDocuments = this.activeBuyDocument ? [this.activeBuyDocument] : this.buyDocuments;
        let buyPositionsMap: Map<string, SalesDocumentPosition> = new Map();
        if (this.activeBuyDocument) {
            const activeBuyPositions = (
                this.connectedBuyPositions[this.activeBuyDocument.uuid] || []
            ).reduce((acu, el) => {
                acu.set(el.uuid, true);
                return acu;
            }, new Map<string, true>());
            this.buyPositionsMap.forEach((value, key) => {
                if (value && activeBuyPositions.has(value.uuid)) {
                    buyPositionsMap.set(key, value);
                }
            });
        } else {
            buyPositionsMap = this.buyPositionsMap;
        }
        if (this.salesDocument && buyPositionsMap.size > 0) {
            this.summary.buySales = DocumentSummarize.summarize(
                this.salesDocument,
                this.positions,
                true,
                buyPositionsMap,
                !this.isComparable
            );

            this.summary.buySales.taxes.forEach((tax) => {
                const taxRow = this.summary.taxes.find((el) => el.taxRate === tax.taxRate);
                if (!taxRow) {
                    this.summary.taxes.push({
                        taxRate: tax.taxRate,
                        sales: null,
                        buySales: tax,
                        buy: null,
                    });
                } else {
                    taxRow.buySales = tax;
                }
            });

            const buySalesGrouping = this.summary.buySales.groupings.find(
                (el) => el.groupSchemaId === +this.summingGroupingSchemaId
            );
            if (buySalesGrouping) {
                buySalesGrouping.groups.forEach((group) => {
                    const groupsRow = this.summary.groups.find((el) => el.key === group.key);
                    if (!groupsRow) {
                        this.summary.groups.push({
                            key: group.key,
                            name: group.name,
                            sales: null,
                            buySales: group,
                            buy: null,
                        });
                    } else {
                        groupsRow.buySales = group;
                    }
                });
            }

            this.summary.buySales.groupsPositions.forEach((gp) => {
                const row = this.summary.groupsPositions.find(
                    (el) => el.groupSchemaId === gp.groupSchemaId
                );
                if (!row) {
                    this.summary.groupsPositions.push({
                        groupSchemaId: gp.groupSchemaId,
                        name: gp.name,
                        sales: null,
                        buySales: gp,
                        buy: null,
                    });
                } else {
                    row.buySales = gp;
                }
            });
        }

        if (buyDocuments?.length) {
            this.summary.buy = DocumentSummarize.summarize(
                buyDocuments[0],
                (this.mode === 'purchase'
                    ? this.buyPositionsPerDocument[buyDocuments[0].uuid]
                    : this.connectedBuyPositions[buyDocuments[0].uuid]) || [],
                false,
                undefined,
                this.isPurchaseComparable
            );

            this.summary.buy.taxes.forEach((tax) => {
                const taxRow = this.summary.taxes.find((el) => +el.taxRate === +tax.taxRate);
                if (!taxRow) {
                    this.summary.taxes.push({
                        taxRate: tax.taxRate,
                        sales: null,
                        buySales: null,
                        buy: tax,
                    });
                } else {
                    taxRow.buy = tax;
                }
            });

            let buyGrouping = this.summary.buy.groupings.find(
                (el) => el.groupSchemaId === +this.summingGroupingSchemaId
            );
            if (buyGrouping) {
                buyGrouping.groups.forEach((group) => {
                    const groupsRow = this.summary.groups.find((el) => el.key === group.key);
                    if (!groupsRow) {
                        this.summary.groups.push({
                            key: group.key,
                            name: group.name,
                            sales: null,
                            buySales: null,
                            buy: group,
                        });
                    } else {
                        groupsRow.buy = group;
                    }
                });
            }

            this.summary.buy.groupsPositions.forEach((gp) => {
                const row = this.summary.groupsPositions.find(
                    (el) => el.groupSchemaId === gp.groupSchemaId
                );
                if (!row) {
                    this.summary.groupsPositions.push({
                        groupSchemaId: gp.groupSchemaId,
                        name: gp.name,
                        sales: null,
                        buySales: null,
                        buy: gp,
                    });
                } else {
                    row.buy = gp;
                }
            });

            for (let i = 1; i < buyDocuments.length; i++) {
                const summary = DocumentSummarize.summarize(
                    buyDocuments[i],
                    (this.mode === 'purchase'
                        ? this.buyPositionsPerDocument[buyDocuments[i].uuid]
                        : this.connectedBuyPositions[buyDocuments[i].uuid]) || [],
                    false,
                    undefined,
                    this.isPurchaseComparable
                );
                this.summary.buy.positions += summary.positions;
                this.summary.buy.totalBeforeDiscountsAndPromotions += summary.totalBeforeDiscountsAndPromotions;
                this.summary.buy.promotion.value += summary.promotion.value;
                this.summary.buy.promotion.promotion = core.round(
                    (this.summary.buy.promotion.value / this.summary.buy.totalBeforeDiscountsAndPromotions) * 100
                );
                this.summary.buy.totalAfterPromotions += summary.totalAfterPromotions;
                this.summary.buy.totalAfterPositionsDiscounts +=
                    summary.totalAfterPositionsDiscounts;
                this.summary.buy.totalAfterDiscounts += summary.totalAfterDiscounts;
                this.summary.buy.totalGrossAfterDiscounts += summary.totalGrossAfterDiscounts;
                this.summary.buy.discount.value += summary.discount.value;
                this.summary.buy.discount.discount = core.round(
                    (this.summary.buy.discount.value / this.summary.buy.totalAfterPromotions) * 100
                );
                this.summary.buy.additionalDiscount.value += summary.additionalDiscount.value;
                this.summary.buy.additionalDiscount.discount = core.round(
                    (this.summary.buy.additionalDiscount.value /
                        this.summary.buy.totalAfterPositionsDiscounts) *
                        100
                );
                this.summary.buy.totalDiscount += summary.totalDiscount;

                this.summary.buy.totalTax += summary.totalTax;

                this.summary.buy.hasNonValuatedPositions = this.summary.buy.hasNonValuatedPositions || summary.hasNonValuatedPositions;
                this.summary.buy.nonValuatedPositionsCount += summary.nonValuatedPositionsCount;

                summary.taxes.forEach((tax) => {
                    const taxRow = this.summary.taxes.find((el) => +el.taxRate === +tax.taxRate);
                    if (!taxRow) {
                        this.summary.taxes.push({
                            taxRate: tax.taxRate,
                            sales: null,
                            buySales: null,
                            buy: tax,
                        });
                    } else {
                        if (taxRow.buy) {
                            taxRow.buy.tax += tax.tax;
                            taxRow.buy.value += tax.value;
                        } else {
                            taxRow.buy = tax;
                        }
                    }
                });

                buyGrouping = summary.groupings.find(
                    (el) => el.groupSchemaId === +this.summingGroupingSchemaId
                );
                if (buyGrouping) {
                    buyGrouping.groups.forEach((group) => {
                        const groupsRow = this.summary.groups.find((el) => el.key === group.key);
                        if (!groupsRow) {
                            this.summary.groups.push({
                                key: group.key,
                                name: group.name,
                                sales: null,
                                buySales: null,
                                buy: group,
                            });
                        } else {
                            if (groupsRow.buy) {
                                groupsRow.buy.value += group.value;
                            } else {
                                groupsRow.buy = group;
                            }
                        }
                    });
                }

                summary.groupsPositions.forEach((gp) => {
                    const row = this.summary.groupsPositions.find(
                        (el) => el.groupSchemaId === gp.groupSchemaId
                    );
                    if (!row) {
                        this.summary.groupsPositions.push({
                            groupSchemaId: gp.groupSchemaId,
                            name: gp.name,
                            sales: null,
                            buySales: null,
                            buy: gp,
                        });
                    } else {
                        if (row.buy) {
                            row.buy.total += gp.total;
                        } else {
                            row.buy = gp;
                        }
                    }
                });
            }
        }

        if (this.summary.buy && this.summary.buySales) {
            this.summary.result.value =
                this.summary.buySales.totalAfterDiscounts -
                this.exchangeRateService.exchange(
                    this.summary.buy.totalAfterDiscounts,
                    this.summary.buy.currency,
                    this.summary.buySales.currency,
                    true
                );
            this.summary.result.margin =
                this.summary.result.value / this.summary.buySales.totalAfterDiscounts;
            this.summary.result.overhead =
                this.summary.result.value /
                this.exchangeRateService.exchange(
                    this.summary.buy.totalAfterDiscounts,
                    this.summary.buy.currency,
                    this.summary.buySales.currency,
                    true
                );
        }
    }

    private getBuyTaxRatesIds() {
        const taxRatesIds: number[] = [];
        this.buyDocuments.forEach((document) => {
            (this.connectedBuyPositions[document.uuid] || []).forEach((position) => {
                if (!taxRatesIds.includes(position.priceTaxRateId)) {
                    taxRatesIds.push(position.priceTaxRateId);
                }
            });
        });
        return taxRatesIds;
    }

    private getSalesTaxRatesIds() {
        const taxRatesIds: number[] = [];
        this.positions.forEach((position) => {
            if (!taxRatesIds.includes(position.priceTaxRateId)) {
                taxRatesIds.push(position.priceTaxRateId);
            }
        });
        return taxRatesIds;
    }

    async confirmOrder(document: SalesDocument, documentIdToSendEmail: string = null) {
        if (document.salesDocumentTypeId === SalesDocumentType.ServiceOrder) {
            this.placingOrderInProgress = true;
            this.placeServiceOrder(document);
            return;
        }
        if (this.configService.user.orderIntegration !== 'OrdersSynchronization' &&
            document.salesDocumentTypeId === SalesDocumentType.Order &&
            document.isOrderCreated &&
            !document.isOrderPlaced
        ) {
            this.placeOrder(this.buyDocuments.filter((doc) => !doc.isEmpty), documentIdToSendEmail);
            return;
        }

        if (!this.configService.user.blockedOrdering) {
            await this.dialogService
                .open(CreateOrderDialogComponent, {
                    data: {
                        document,
                        buyDocuments: this.buyDocuments.filter((doc) => !doc.isEmpty),
                        documentIdToSendEmail,
                        order: (orderLater, deliveryDate) => this.order(document, orderLater, deliveryDate),
                        placeOrder: (orders, deliveryDate, documentIdToSendEmailLocal) => this.placeOrder(orders, documentIdToSendEmailLocal, deliveryDate),
                        confirm: (orderLater, deliveryDate) => this.confirmOrderDocument(document, deliveryDate, false, orderLater, true),
                        withdrawPlaceOrders: () => this.withdrawPlaceOrders(document, documentIdToSendEmail)
                    },
                })
                .afterClosed()
                .toPromise();
        } else {
            this.toastService.add({
                title: this.translateService.instant('SALES_DOCUMENT|Zamawianie zablokowane'),
                body: this.dynamicElementsService.getElementContentBySlug('blocked-ordering-message', true),
                severity: 'warn',
            });
        }
    }

    async order(document: SalesDocument, orderLater = false, deliveryDate: string = null) {
        if (this.invalidOffer || document.isOrderPlaced || this.processing) {
            return;
        }
        this.processing = true;
        try {
            let validDocuments = true;
            const processedDocuments =
                this.licenseType === 'license' ? [document] : this.buyDocuments;
            for (const buyDocument of processedDocuments) {
                validDocuments =
                    validDocuments && !(await this.documentHasErrors(buyDocument, true));
            }
            if (validDocuments) {
                const newDocuments = await this.salesDocumentService.createOrder(document, orderLater, deliveryDate);
                const orders =
                    this.licenseType === 'license' ? [newDocuments.sales] : newDocuments.buy;
                await this.router.navigate(['trade', 'offer', newDocuments.sales.uuid]);
                if (orderLater) {
                    if (this.configService.config.config.Trade.orderEmails) {
                        this.salesDocumentService.prepareOrderEmail(orders, newDocuments.sales.uuid, orderLater);
                    }
                    return {
                        success: true,
                        orders,
                        documentIdToSendEmail: newDocuments.sales.uuid,
                    };
                } else {
                    const placeResults = await this.salesDocumentService.placeOrders(orders, deliveryDate, newDocuments.sales.uuid);
                    return this.salesDocumentService.afterOrderPlaced(placeResults, orders, newDocuments.sales.uuid);
                }
            } else {
                await this.router.navigateByUrl('/', { skipLocationChange: true });
                await this.router.navigate(['trade', 'offer', document.uuid]);
                return {
                    success: true,
                    orders: [],
                    documentIdToSendEmail: null,
                };
            }
        } finally {
            this.processing = false;
        }
    }

    async placeOrder(processedDocuments: SalesDocument[] = this.buyDocuments, documentIdToSendEmail: string, deliveryDate: string = null,) {
        if (!this.configService.user.blockedOrdering) {
            const possible: SalesDocument[] = await this.getPossibleOrdersToPlace(processedDocuments);
            if (possible?.length > 0) {
                if (!documentIdToSendEmail && this.salesDocument) {
                    documentIdToSendEmail = this.salesDocument.uuid;
                }
                const placeResults = await this.salesDocumentService.placeOrders(possible, deliveryDate, documentIdToSendEmail);
                return this.salesDocumentService.afterOrderPlaced(placeResults, possible, documentIdToSendEmail);
            }
        } else {
            this.toastService.add({
                title: this.translateService.instant('SALES_DOCUMENT|Zamawianie zablokowane'),
                body: this.dynamicElementsService.getElementContentBySlug('blocked-ordering-message', true),
                severity: 'warn',
            });
        }
    }

    async withdrawPlaceOrders(document: SalesDocument, documentIdToSendEmail: string) {
        const possible: SalesDocument[] = await this.getPossibleOrdersToPlace(this.buyDocuments);
        if (possible?.length > 0) {
            await this.salesDocumentService.withdrawPlaceOrders(possible);
            return {
                success: true,
                orders: possible,
                documentIdToSendEmail,
            };
        }
    }

    async placeServiceOrder(document: SalesDocument) {
        if (!this.configService.user.blockedOrdering) {
            const processedDocuments =
                this.licenseType === 'license' ? [document] : this.buyDocuments;
            const possible: SalesDocument[] = await this.getPossibleServiceOrdersToPlace(processedDocuments);
            if (possible?.length > 0) {
                const placeResults = await this.salesDocumentService.placeOrders(possible);
                return this.salesDocumentService.afterOrderPlaced(placeResults, possible, document?.uuid);
            }
        } else {
            this.toastService.add({
                title: this.translateService.instant('SALES_DOCUMENT|Zamawianie zablokowane'),
                body: this.dynamicElementsService.getElementContentBySlug('blocked-ordering-message', true),
                severity: 'warn',
            });
        }
    }

    private async getPossibleOrdersToPlace(processedDocuments: SalesDocument[], allowToPlaceUnconfirmed = false) {
        const possible: SalesDocument[] = [];

        for (const doc of processedDocuments) {
            const { invalidOffer } = this.offerValidityService.getValidDate(doc);
            const canManage = await this.salesDocumentService.hasAccessToOrder(doc, 'manage');
            if (canManage &&
                !invalidOffer &&
                !doc.isOrderPlaced &&
                !doc.isAnnexed &&
                !doc.isCanceled &&
                (doc.isConfirmed || allowToPlaceUnconfirmed) &&
                doc.externalSource !== 'ERP') {
                possible.push(doc);
            }
        }
        return possible;
    }

    private async getPossibleServiceOrdersToPlace(processedDocuments: SalesDocument[], allowToPlaceUnconfirmed = false) {
        const possible: SalesDocument[] = [];

        for (const doc of processedDocuments) {
            const canManage = await this.salesDocumentService.hasAccessToManageServiceOrder(doc);
            if (canManage &&
                !doc.isOrderPlaced &&
                !doc.isAnnexed &&
                !doc.isCanceled &&
                doc.externalSource !== 'ERP') {
                possible.push(doc);
            }
        }
        return possible;
    }

    async inquireValuation(processedDocuments: SalesDocument[] = this.buyDocuments) {
        if (this.processing) {
            return;
        }
        this.processing = true;
        const possible: SalesDocument[] = [];
        try {
            for (const doc of processedDocuments) {
                if (
                    !doc.isOrderPlaced &&
                    !doc.isCanceled &&
                    doc.hasNonValuatedPositions &&
                    doc.externalSource !== 'ERP'
                ) {
                    possible.push(doc);
                }
            }
            if (possible?.length > 0) {
                for (const buyDocument of possible) {
                    await this.documentValuationService.inquireValuation(buyDocument);
                }
            }
        } finally {
            this.processing = false;
        }
    }

    async confirmSendValuation(document: SalesDocument) {
        const result = await this.dialogService
            .open(SendValuationDialogComponent, {
                data: {
                    document,
                    hasNonValuatedPositions: document.hasNonValuatedPositions,
                },
            })
            .afterClosed()
            .toPromise();

        if (result) {
            this.sendValuation(document, result.validityDate);
        }
    }

    async sendValuation(document: SalesDocument, validityDate: Date) {
        if (this.processing) {
            return;
        }
        this.processing = true;
        try {
            await this.documentValuationService.sendValuation(document, validityDate);
        } finally {
            this.processing = false;
        }
    }

    async consult(processedDocuments: SalesDocument[] = this.buyDocuments) {
        const possible: SalesDocument[] = [];

        for (const doc of processedDocuments) {
            if (
                doc.isConfirmed &&
                !doc.isAnnexed &&
                !doc.isOrderPlaced &&
                !doc.isCanceled &&
                !doc.hasNonValuatedPositions &&
                doc.externalSource !== 'ERP'
            ) {
                possible.push(doc);
            }
        }
        if (possible?.length > 0) {
            for (const buyDocument of possible) {
                this.salesDocumentService.consult(buyDocument);
            }
        }
    }

    async copy(document: SalesDocument) {
        if (!this.processing) {
            this.processing = true;
            this.store.dispatch(startBuffering());
            const {
                sales: newDocument,
            } = await this.documentCreationService.duplicateSalesAndBuyDocuments(document);
            this.store.dispatch(stopBuffering());
            await this.router.navigate(['trade', 'offer', newDocument.uuid]);
            this.toastService.add({
                title: this.translateService.instant('SALES_DOCUMENT|Kopiowanie dokumentu'),
                body: this.translateService.instant(
                    'SALES_DOCUMENT|Dokument {sourceNumber} został zduplikowany. Nowa wersja dokumentu {targetNumber} jest otwarta do edycji.',
                    {
                        sourceNumber: document.documentNumber?.join(''),
                        targetNumber: newDocument.documentNumber?.join(''),
                    }
                ),
                severity: 'info',
            });
        }
    }

    async confirmModify(document: SalesDocument, onlySales = false, onlyOwn = false) {
        let neededConfirm = false;
        if (document.salesDocumentTypeId === SalesDocumentType.Order) {
            const processedDocuments =
                this.licenseType === 'license' ? [document] : this.buyDocuments;
            for (const doc of processedDocuments) {
                const { invalidOffer } = this.offerValidityService.getValidDate(doc);
                neededConfirm = neededConfirm || invalidOffer;
            }
        }

        if (
            document.salesDocumentTypeId === SalesDocumentType.Order &&
            (neededConfirm || this.invalidVersion || this.configService.user.orderIntegration === 'OrdersSynchronization' && this.licenseType === 'license')
        ) {
            if (this.configService.user.orderIntegration === 'OrdersSynchronization' && this.licenseType === 'license') {
                const result = await this.dialogService
                    .open(ModifyOrderDialogComponent, {
                        data: {
                            document,
                            buyDocuments: this.buyDocuments.filter((doc) => !doc.isEmpty),
                        },
                    })
                    .afterClosed()
                    .toPromise();
                if (result?.mode) {
                    this.modify(document, onlySales, this.buyDocuments.length > 0, result?.mode);
                }
            } else {
                const title = this.translateService.instant('SALES_DOCUMENT|Modyfikacja zamówienia');
                const body = this.translateService.instant(
                    'SALES_DOCUMENT|W zmodyfikowanym zamówieniu zostaną przeliczone wszystkie pozycje wg aktualnie uruchomionego cennika. '
                );

                const bodyExpansion = this.translateService.instant(
                    'SALES_DOCUMENT|Wszystkie pozycje zamówienia zostaną przeliczone. <br><br>Jeśli cena uległa zmianie, to cena pozycji się zmieni, jeśli nie to cena pozostanie niezmieniona.'
                );

                this.toastService.add({
                    key: this.RECALCULATION_CONFIRMATION_TOAST,
                    title,
                    body,
                    bodyExpansion,
                    severity: 'question',
                    sticky: true,
                    buttons: [
                        {
                            title: this.translateService.instant('INTERFACE|Kontynuuj'),
                            onClick: () =>
                                this.modify(document, onlySales, this.buyDocuments.length > 0),
                        },
                        {
                            title: this.translateService.instant('INTERFACE|Anuluj'),
                            onClick: () => null,
                        },
                    ],
                });
            }
        } else if (onlySales) {
            const title = this.translateService.instant('SALES_DOCUMENT|Modyfikacja zamówienia klienta końcowego');
            let body = this.translateService.instant(
                'SALES_DOCUMENT|Modyfikujesz wyłącznie dokument sprzedaży, na którym możesz modyfikować tylko produkty własne.'
            );

            if (this.licenseType !== 'license') {
                body = this.translateService.instant(
                    'SALES_DOCUMENT|Modyfikujesz wyłącznie dokument sprzedaży, na którym możesz modyfikować tylko produkty własne. Nie możesz modyfikować zamówienia u dostawcy. Aby zmodyfikować dokument zakupowy należy skontaktować się z dostawcą.'
                );
            }

            this.toastService.add({
                key: this.MODIFY_CONFIRMATION_TOAST,
                title,
                body,
                severity: 'question',
                sticky: true,
                buttons: [
                    {
                        title: this.translateService.instant('INTERFACE|Kontynuuj'),
                        onClick: () =>
                            this.modify(document, onlySales, this.buyDocuments.length > 0),
                    },
                    {
                        title: this.translateService.instant('INTERFACE|Anuluj'),
                        onClick: () => null,
                    },
                ],
            });
        } else {
            return this.modify(document, onlySales, this.buyDocuments.length > 0);
        }
    }

    async modify(document: SalesDocument, onlySales = false, onlyOwn = false, allowEditBy: 'all' | 'sales' = 'sales') {
        if (!this.processing) {
            this.processing = true;
            let newDocument: SalesDocument;
            if (!onlySales) {
                const {
                    sales: newSalesDocument,
                } = await this.documentCreationService.duplicateSalesAndBuyDocuments(
                    document,
                    true,
                    false,
                    document.salesDocumentTypeId,
                    false,
                    allowEditBy
                );
                newDocument = newSalesDocument;
            } else {
                const { newSalesDocument } = await this.documentCreationService.duplicateSalesOrder(
                    document,
                    onlyOwn,
                    null,
                    null,
                    allowEditBy,
                );
                newDocument = newSalesDocument;
            }
            if (
                this.offerRecalculationMethod() === 'wholeOffer' &&
                newDocument.salesDocumentTypeId !== SalesDocumentType.Order &&
                newDocument.salesDocumentTypeId !== SalesDocumentType.Inquiry
            ) {
                await this.documentPricesService.recalculatePosition(newDocument);
            } else if (
                newDocument.salesDocumentTypeId === SalesDocumentType.Order &&
                (this.invalidOffer || this.invalidVersion)
            ) {
                await this.documentPricesService.recalculatePosition(newDocument);
            }
            this.documentManagerService.tagChanges(newDocument.uuid, {
                uuid: core.generateUUID(),
                type: 'documentModification',
                extras: {
                    allowEditBy,
                }
            });

            await this.router.navigate(['trade', 'offer', newDocument.uuid]);
            this.processing = false;
            if (onlySales) {
                this.switchMode('sale');
            }
            this.toastService.add({
                title: this.translateService.instant('SALES_DOCUMENT|Modyfikacja dokumentu'),
                body: this.translateService.instant(
                    'SALES_DOCUMENT|Dokument {sourceNumber} został zduplikowany. Nowa wersja dokumentu {targetNumber} jest otwarta do edycji.',
                    {
                        sourceNumber: document.documentNumber?.join(''),
                        targetNumber: newDocument.documentNumber?.join(''),
                    }
                ),
                severity: 'info',
            });
        }
    }

    confirmRecalculate(document: SalesDocument) {
        const title = this.translateService.instant('SALES_DOCUMENT|Przeliczenie cen oferty');
        const body = this.translateService.instant(
            'SALES_DOCUMENT|Przeliczone zostaną wszystkie pozycje wg aktualnie uruchomionego cennika.'
        );

        const bodyExpansion = this.translateService.instant(
            'SALES_DOCUMENT|Wszystkie pozycje oferty zostaną przeliczone. <br><br>Jeśli cena uległa zmianie, to cena pozycji się zmieni, jeśli nie to cena pozostanie niezmieniona. <br><br>Aby szczegółowo sprawdzić różnice konieczne jest uprzednie wygenerowanie oferty przed aktualizacją. <br><br>Porównanie zmian po aktualizacji będzie możliwe tylko, jeśli poprzednia wersja była zatwierdzona.'
        );

        this.toastService.add({
            key: this.RECALCULATION_CONFIRMATION_TOAST,
            title,
            body,
            bodyExpansion,
            severity: 'question',
            sticky: true,
            buttons: [
                {
                    title: this.translateService.instant('INTERFACE|Kontynuuj'),
                    onClick: () => this.recalculate(document),
                },
                {
                    title: this.translateService.instant('INTERFACE|Anuluj'),
                    onClick: () => null,
                },
            ],
        });
    }

    async recalculate(document: SalesDocument, priceVersion = false) {
        if (!this.calculationInProgress) {
            this.calculationInProgress = true;
            try {
                if (document.isConfirmed) {
                    const {
                        sales: newDocument,
                    } = await this.documentCreationService.duplicateSalesAndBuyDocuments(document, true);
                    await this.documentPricesService.recalculatePosition(newDocument, null, true);
                    this.documentManagerService.tagChanges(newDocument.uuid, {
                        uuid: core.generateUUID(),
                        type: 'documentModification',
                        extras: {
                            allowEditBy: 'sales',
                        }
                    });
                    await this.router.navigate(['trade', 'offer', newDocument.uuid]);
                } else {
                    this.invalidUnitsInDocument = await this.documentPricesService.recalculatePosition(
                        document,
                        null,
                        true
                    );
                }
                this.changingActivePriceVersion = null;
            } finally {
                this.calculationInProgress = false;
            }
        }
    }

    confirmDelete(document: SalesDocument) {
        const title = this.translateService.instant('SALES_DOCUMENT|Potwierdzenie usunięcia');
        const body = this.translateService.instant(
            'SALES_DOCUMENT|Czy chcesz usunąć ten dokument?'
        );

        this.toastService.add({
            title,
            body,
            severity: 'question',
            sticky: true,
            buttons: [
                {
                    title: this.translateService.instant('INTERFACE|Tak'),
                    onClick: () => this.delete(document),
                },
                {
                    title: this.translateService.instant('INTERFACE|Nie'),
                    onClick: () => null,
                },
            ],
        });
    }

    async delete(document: SalesDocument) {
        this.salesDocumentService.deleteWithPositions(document);
        if (document.salesDocumentTypeId === SalesDocumentType.ServiceOrder) {
            this.router.navigate(['trade', 'offer', 'list', 'service-orders']);
        } else {
            this.router.navigate(['trade', 'offer', 'list', 'offers']);
        }
    }

    async transferChanges() {
        if (this.processing) {
            return;
        }
        this.processing = true;
        try {
            if (this.salesDocument && this.buyDocuments.length > 0 && !this.integral) {
                const newDocument = await this.salesDocumentService.transferChanges(this.salesDocument);
                await this.router.navigate(['trade', 'offer', newDocument.uuid]);
                this.switchMode('sale');
            }
        } finally {
            this.processing = false;
        }
    }

    loadCurrencies() {
        this.currencies = this.exchangeRateService.currencies;
        this.buyCurrencies = this.exchangeRateService.buyCurrencies;
        this.salesCurrenciesFromBuy = this.exchangeRateService.salesCurrenciesFromBuy;
        this.canChangeSaleCurrency = this.exchangeRateService.canChangeSaleCurrency;
    }

    loadExchangeRates() {
        this.exchangeRates = this.exchangeRateService.exchangeRates;
        this.buyExchangeRates = this.exchangeRateService.buyExchangeRates;
    }

    get licenseType() {
        return this.configService.user.user.ActiveCompanyLicenseType;
    }

    chargeAvailable() {
        return this.configService.user.chargeAvailable;
    }

    offerRecalculationMethod() {
        return this.configService.user.offerRecalculationMethod;
    }

    async loadOwnProducts() {
        this.ownProducts = await this.ownProductsService.getOwnProducts();
    }

    openOwnProductsDialog() {
        this.dialogService
            .open(OwnProductsDialogComponent, {
                data: {
                    products: this.ownProducts,
                },
            })
            .afterClosed()
            .toPromise()
            .then((result) => {
                if (result) {
                    this.addOwnProduct(result);
                }
            });
    }

    async addOwnProduct(value: number) {
        if (value) {
            const product = this.ownProducts.find((p) => p.id === value);
            const unitInfo = getUnitInfo(product.price.priceUnits[0]?.code);
            await this.salesDocumentService.addPositionToActiveDocumentFromProduct(
                product,
                product.price,
                product.price.value,
                1,
                {
                    id: product.price.priceUnits[0]?.id,
                    code: product.price.priceUnits[0]?.code,
                    symbol: unitInfo.symbol,
                },
            );
            this.salesDocumentService.scrollToPosition();
        }
    }

    changeCurrency(currencyCode: string) {
        this.salesDocumentService.changeCurrency(this.salesDocument, currencyCode);
    }

    changeBuyCurrency(currencyCode: string) {
        this.salesDocumentService.changeBuyCurrency(
            this.buyDocuments,
            currencyCode,
            this.salesDocument
        );
    }

    printPdf(download = false) {
        if (this.configService.user.hasPrintTemplates) {
            this.printTemplatedPdf(download);
        } else {
            const salesDocument =
                this.mode === 'purchase' && this.activeBuyDocument
                    ? this.activeBuyDocument
                    : this.openedDocument;
            const printUrl = `${window.location.origin}/api/pdf/trade/${salesDocument.uuid}?sortingOrder=${
                    this.sortingMode
                }&groupingSchemaId=${this.groupingSchemaId}${
                    this.mode === 'purchase' && this.salesDocument && !this.activeBuyDocument ? '&allBuyDocuments=1' : ''
                }&print=${download ? 0 : 1}`;
            if (download) {
                window.open(
                    printUrl,
                    '_blank'
                );
            } else {
                this.print(printUrl);
            }
        }
    }

    async printTemplatedPdf(download = false) {
        let salesDocument = this.openedDocument;

        if (this.mode === 'purchase') {
            if (this.activeBuyDocument) {
                salesDocument = this.activeBuyDocument;
            } else if (this.buyDocuments.length === 1) {
                salesDocument = this.buyDocuments[0];
            }
        }
        let positions = this.positions;
        if (this.mode === 'purchase') {
            if (this.activeBuyDocument) {
                positions = this.buyPositionsPerDocument[this.activeBuyDocument.uuid];
            } else if (this.buyDocuments.length === 1) {
                positions = this.connectedBuyPositions[this.buyDocuments[0].uuid];
            }
        }

        const attachments = await firstValueFrom(this.store.select(getDocumentsAttachments([salesDocument.uuid])));

        this.dialogService.open(PrintTemplatedDialogComponent, {
            data: {
                document: salesDocument,
                positions,
                mode: download ? 'download' : 'print',
                selectBuyDocument: this.mode === 'purchase' && this.buyDocuments.length > 1 && !this.activeBuyDocument,
                buyDocuments: this.buyDocuments,
                departments: this.personsService.getDepartments(),
                sellerCompanyName: (this.mode !== 'purchase'
                    ? this.configService.user.company
                    : this.configService.user.accountManagerCompany)?.name,
                hasAttachments: attachments.size > 0,
            },
        }).afterClosed().toPromise().then((result) => {
            if (result?.type) {
                let pdfUrl = ``;

                if (result?.type === 'templated') {
                    const selectedTemplates: Record<string, number> = result?.selectedTemplates;
                    const templatesQuery = Object.entries(selectedTemplates).reduce((acc, [key, value]) => {
                        if (value) {
                            acc.append('t[' + key + ']', String(value));
                        }
                        return acc;
                    }, new URLSearchParams());
                    const documentId = result.documentId || (this.mode === 'purchase' && this.activeBuyDocument && this.activeBuyDocument.uuid) || salesDocument.uuid;
                    const printSettingsQuery = new URLSearchParams(result.printSettings);
                    pdfUrl = `${window.location.origin}/api/pdf/templated/${documentId}?sortingOrder=${
                            this.sortingMode
                        }&groupingSchemaId=${this.groupingSchemaId}&print=${download ? 0 : 1}&${templatesQuery.toString()}&mainTemplate=${result.selectedMainTemplate}&${printSettingsQuery.toString()}`;
                } else {
                    pdfUrl = `${window.location.origin}/api/pdf/trade/${salesDocument.uuid}?sortingOrder=${
                        this.sortingMode
                    }&groupingSchemaId=${this.groupingSchemaId}${
                        this.mode === 'purchase' && this.salesDocument && !this.activeBuyDocument ? '&allBuyDocuments=1' : ''
                    }&print=${download ? 0 : 1}`;
                }

                if (!download) {
                    this.toastService.add({
                        title: this.translateService.instant('PRINT|Generowanie podglądu'),
                        body: this.translateService.instant('PRINT|Trwa generowanie podglądu dokumentu. Proszę czekać...'),
                        severity: 'info',
                    });
                }

                if (!download) {
                    this.print(pdfUrl);
                    return;
                }
                window.open(
                    pdfUrl,
                    '_blank'
                );
            }
        });
    }

    print(url: string) {
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.width = '100%';
        iframe.style.height = '100%';
        iframe.style.border = 'none';
        iframe.src = url;

        const windowFocusHandler = () => {
            document.body.removeChild(iframe);
            window.removeEventListener('focus', windowFocusHandler);
        }

        window.addEventListener('focus', windowFocusHandler);

        iframe.onload = function() {
            const contentWindow = iframe?.contentWindow;
            if (contentWindow) {
                contentWindow.print();
            }
        };
        document.body.appendChild(iframe);
        return;
    }

    printExternalVenetianOrderPdf(salesDocument: SalesDocument) {
        const document = this.buyDocuments[0] || this.salesDocument;
        if (document) {
            window.open(
                `${window.location.origin}/api/pdf/external-venetian-order/${document.uuid}?sortingOrder=${this.sortingMode}`,
                '_blank'
            );
        }
    }

    showIntegrityWarning() {
        if (this.alertElement) {
            this.alertElement.nativeElement.scrollIntoView();
            this.alertElement.nativeElement.classList.add('blink');
            setTimeout(() => {
                if (this.alertElement?.nativeElement) {
                    this.alertElement.nativeElement.classList.remove('blink');
                }
            }, 2000);
        }
    }

    private setErrorsMessage(title: string, errors: string[], sticky = true) {
        this.toastService.add({
            title,
            body: errors.join('<br>'),
            severity: 'error',
            sticky,
        });
    }

    private setDocumentErrorsMessage(documentErrors: DocumentErrors) {
        this.toastService.add({
            title: this.translateService.instant('SALES_DOCUMENT|Zamówienie nie zostało wysłane.'),
            body: this.translateService.instant('SALES_DOCUMENT|Popraw dane modyfikując dokument.'),
            severity: 'error',
            sticky: true,
        });

        let errors = [];
        if (documentErrors.errors.length) {
            documentErrors.errors;
        }
        if (documentErrors.client.errors.length) {
            errors = errors.concat(documentErrors.client.errors);
        }
        if (documentErrors.client.invoice.errors.length) {
            errors = errors.concat(documentErrors.client.invoice.errors);
        }
        if (documentErrors.client.contact.errors.length) {
            errors = errors.concat(documentErrors.client.contact.errors);
        }
        if (errors.length) {
            this.setErrorsMessage(
                this.translateService.instant('SALES_DOCUMENT|Błąd w danych'),
                errors
            );
        }
    }

    private async documentHasErrors(
        document: SalesDocument,
        setDocumentErrorsMessage = false
    ): Promise<boolean> {
        const documentErrors = validationDocument(document, this.translateService);
        if (
            documentErrors.errors.length !== 0 ||
            documentErrors.client.errors.length !== 0 ||
            documentErrors.client.contact.errors.length !== 0 ||
            documentErrors.client.invoice.errors.length !== 0
        ) {
            if (setDocumentErrorsMessage) {
                this.setDocumentErrorsMessage(documentErrors);
            }
            return true;
        } else {
            return false;
        }
    }

    addCalculationInfo() {
        this.toastService.add({
            title: this.translateService.instant('SALES_DOCUMENT|Tryb przeliczania'),
            body: this.getCalculationInfo(),
            severity: 'info',
        });
    }

    getCalculationInfo(): string {
        return this.translateService.instant(
            'SALES_DOCUMENT|Uruchomiono tryb przeliczania całej oferty po każdej edycji (każdej edycji pozycji, dodaniu pozycji, kopiowaniu pozycji, zmianie rabatu pozycji, zmianie waluty sprzedaży, zmianie wersji cenowej, zmiany podatku vat, zmianie kalkulacji vat oraz stawki vat ). W ten sposób zapewniamy spójność oferty, ale może to powodować zmianę ceny już po dodaniu danej pozycji do oferty. Jeśli oczekiwana jest gwarancja podanej ceny, należy zatwierdzić ofertę. Zatwierdzona oferta daje gwarancję ceny do końca daty ważności oferty.'
        );
    }

    setSalesDocumentSellerCompanyLicense() {
        if (this.openedDocument?.sellerCompanyId) {
            this.salesDocumentSellerCompanyLicense = this.companyService?.getLicense(
                this.openedDocument.sellerCompanyId
            );
        }
    }

    get buyIsInModification() {
        return this.salesDocument?.salesDocumentTypeId === SalesDocumentType.Order &&
            this.buyDocuments.some((document) => !document.isConfirmed);
    }

    async openPriceVersionDialog() {
        const result = await this.dialogService
            .open(PriceVersionDialogComponent, {
                data: {
                    priceVersion: this.priceVersionObj,
                    document: this.salesDocument,
                    priceUpdatePending: false,
                    invalidVersion: this.invalidVersion,
                },
            })
            .afterClosed()
            .toPromise();

        if (result?.id) {
            this.changingActivePriceVersion = this.salesDocument?.uuid;
            await this.priceVersionService.activatePriceVersionData(result.id);
            this.confirmRecalculate(this.salesDocument);
        }
    }

    async canBeCreatedSparePartsDocuments() {
        for (const position of this.positions) {
            if (position.type === 'static' || position.type === 'additional') {
                continue;
            }
            if (!this.configuratorLoaderService.configuratorsRepositoryServiceStatic) {
                return false;
            }
            try {
                const configuratorId = position.type === 'complex' ? position.configuration.configuratorId : 0;
                await this.configuratorLoaderService.configuratorsRepositoryService.loadConfigurator(
                    position.type,
                    configuratorId
                );
                if (await this.configuratorLoaderService.configuratorsRepositoryServiceStatic.getSpareParts(position.type, configuratorId)?.applicable(position.configuration)) {
                    return true;
                };
            } catch (error) {
                logger.error(error);
            }
        }

        return false;
    }

    async createSparePartsDocument(salesDocument: SalesDocument) {
        const applicablePositions: SalesDocumentPosition[] = [];
        const configuratorsIndex: Record<string, { type: string, configuratorId: number }> = {};
        for (const position of this.positions) {
            if (position.type === 'static' || position.type === 'additional') {
                continue;
            }
            if (!this.configuratorLoaderService.configuratorsRepositoryServiceStatic) {
                continue;
            }
            const configuratorId = position.type === 'complex' ? position.configuration.configuratorId : 0;
            await this.configuratorLoaderService.configuratorsRepositoryService.loadConfigurator(
                    position.type,
                    configuratorId
                );
            if (await this.configuratorLoaderService.configuratorsRepositoryServiceStatic.getSpareParts(position.type, configuratorId)?.applicable(position.configuration)) {
                applicablePositions.push(position);
                configuratorsIndex[position.type + '-' + configuratorId ] = { type: position.type, configuratorId };
            };
        }

        const newDocument = await this.salesDocumentService.createDocument();
        newDocument.buyerCompanyId = salesDocument.buyerCompanyId;
        newDocument.buyerContractorId = salesDocument.buyerContractorId;
        newDocument.buyerResourceId = salesDocument.buyerResourceId;
        newDocument.buyerUserId = salesDocument.buyerUserId;
        newDocument.client = core.copy(salesDocument.client);
        newDocument.priceCurrency = salesDocument.priceCurrency;
        const currentOfferValidity = this.offerValidityService.getCurrentOfferValidity(
            newDocument
        );
        const documentPriceVersion = this.priceVersionService.getPriceVersion(
            newDocument.priceVersionId
        );
        this.documentManagerService.addDocument(newDocument);

        if (this.configService.user.user.ActiveCompanyLicenseType !== 'license') {
            const newBuyDocument = this.salesDocumentService.createBuyDocumentFromSalesDocument(
                newDocument,
                null
            );
            this.documentManagerService.addDocument(newBuyDocument);
            this.documentManagerService.joinDocuments(newDocument.uuid, newBuyDocument.uuid);
        }

        for (const configurators of Object.values(configuratorsIndex)) {
            await this.configuratorLoaderService.configuratorModuleRef.instance.loadConfiguratorData(configurators.type, 0, 0, this.configService.config.languageCode ?? 'pl', true);
        }

        const newPositions: SalesDocumentPosition[] = [];
        for (const position of applicablePositions) {
            await this.configuratorLoaderService.configuratorsRepositoryService.loadConfigurator(
                position.configuration.type,
                position.configuration.configuratorId
            );
            const getPositionTitle = this.configuratorLoaderService.configuratorsRepositoryServiceStatic.getPositionTitle(position.configuration.type, position.configuration.configuratorId);
            const newConfiguration = await this.configuratorLoaderService.configuratorsRepositoryServiceStatic.getSpareParts(position.configuration.type, position.configuration.configuratorId).getSparePartsConfiguration(position.configuration);
            let newTitle: string[] = [];
            if (typeof getPositionTitle === 'function') {
                newTitle = getPositionTitle(newConfiguration, this.translateService.instant.bind(this.translateService));
            }
            const newPosition = await this.positionService.createPositionFromConfiguration(
                newDocument,
                newDocument.priceCurrency,
                newConfiguration,
                newTitle,
                documentPriceVersion,
                currentOfferValidity,
                newPositions,
            );
            newPositions.push(newPosition);
            this.documentManagerService.addPosition(newPosition, newDocument);

            if (
                !newDocument.sellerProductDepartmentId &&
                this.configService.user.user.ActiveCompanyLicenseType === 'license'
            ) {
                newDocument.sellerProductDepartmentId = newPosition.department?.id;
                this.documentManagerService.saveDocument(newDocument.uuid, {
                    sellerProductDepartmentId: newPosition.department?.id,
                });
            }
        }
        await this.documentPricesService.recalculatePosition(newDocument, [], false, false, true);

        await this.router.navigate(['trade', 'offer', newDocument.uuid]);
    }

    async fixUnremovedPositions() {
        if (this.integral) {
            return;
        }

        if (!this.hasAccessToBuy) {
            return;
        }

        if (!this.hasAccessTo.deletePositionsBothSides) {
            return;
        }

        const buyPositionsToRemove: SalesDocumentPosition[] = [];

        for (const position of this.allPositions) {
            if (!position?.isDeleted) {
                continue;
            }
            const buyPosition = this.allBuyPositionsMap.get(position.uuid);
            if (buyPosition && !buyPosition.isDeleted) {
                buyPositionsToRemove.push(buyPosition);
            }
        }

        if (buyPositionsToRemove.length === 0) {
            return;
        }

        const buyDocumentsMap = new Map<string, SalesDocument>();
        for (const buyDocument of this.buyDocuments) {
            buyDocumentsMap.set(buyDocument.uuid, buyDocument);
        }

        for (const buyPosition of buyPositionsToRemove) {
            await this.salesDocumentService.removePosition(buyPosition, buyDocumentsMap.get(buyPosition.documentId));
        }
    }

    loadStatus() {
        this.documentStatuses =
            this.configService.user.statusSystems[
                this.buyDocuments[0]?.salesDocumentTypeId
            ]?.statuses ?? [];
    }

    getStatus(document: SalesDocument): DocumentStatus | undefined {
        return this.documentStatuses.find((el) => Number(el.id) === Number(document?.statusId));
    }

    /**
     * Sprawdza czy pokazać przycisk "Przejdź do zamówienia" dla zamówionej oferty
     */
    showGoToOrderButton(): boolean {
        return this.salesDocument?.isOrderPlaced &&
               this.salesDocument?.salesDocumentTypeId === SalesDocumentType.Quotation &&
               this.latestOrderId !== null;
    }

    /**
     * Sprawdza czy pokazać przycisk "Przejdź do aktualnego zamówienia" dla aneksowanego zamówienia
     */
    showGoToCurrentOrderButton(): boolean {
        return this.salesDocument?.isAnnexed &&
               this.salesDocument?.salesDocumentTypeId === SalesDocumentType.Order &&
               this.latestOrderId !== null;
    }

    /**
     * Przechodzi do najnowszego zamówienia
     */
    async goToLatestOrder(): Promise<void> {
        if (this.latestOrderId) {
            this.router.navigate(['trade', 'offer', this.latestOrderId], {
                state: {
                    documentType: SalesDocumentType.Order,
                    side: 'sales',
                    unplaced: false,
                },
            });
        } else {
            this.toastService.add({
                title: this.translateService.instant('SALES_DOCUMENT|Błąd'),
                body: this.translateService.instant('SALES_DOCUMENT|Nie znaleziono aktualnego zamówienia'),
                severity: 'error',
            });
        }
    }

    /**
     * Znajduje ID najnowszego zamówienia w łańcuchu dokumentów
     */
    private async findLatestOrderId(): Promise<string | null> {
        if (!this.salesDocument) {
            return null;
        }

        const latestOrderId = await this.documentManagerService.findLatestOrderId(this.salesDocument);
        if (!latestOrderId) {
            return null;
        }
        let document = await this.syncService.getOrLoadDocument(latestOrderId, getSalesDocument);
        if (!document) {
            await this.syncService.addCurrentDocumentId(latestOrderId);
            await this.syncService.waitForSynced([latestOrderId]);
            await this.syncService.loadDocuments([latestOrderId]);
        }
        document = await this.syncService.getOrLoadDocument(latestOrderId, getSalesDocument);
        return document ? document.uuid : null;
    }
}
